import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { VehicleProductNameModal } from "@/components/catalog/VehicleProductNameModal";
import { ProductImageGallery } from "@/components/catalog/ProductImageGallery";
import { AttributeSelectControl, AttributeSelectRuntimeAddButton } from "@/components/catalog/AttributeSelectControl";
import { SalePriceInput } from "@/components/catalog/SalePriceField";
import { PurchaseCostCategorySelect } from "@/components/catalog/PurchaseCostCategorySelect";
import { DealInformationSection } from "@/components/catalog/DealInformationSection";
import { CompanyOwnerCommissionSection } from "@/components/catalog/CompanyOwnerCommissionSection";
import { DealPartnersSection } from "@/components/catalog/DealPartnersSection";
import { CreateInvestorDialog } from "@/components/investors/CreateInvestorDialog";
import {
  PaymentMethodDetailFields,
  PaymentMethodSelect,
  applyPaymentMethodChange,
  emptyPaymentMethodDraft,
  type PaymentMethodValue,
} from "@/components/sales/PaymentMethodFields";
import { countryFlagEmoji, fetchCountries, type CountryOption } from "@/lib/geo/geoStore";
import { CountryFlagIcon } from "@/components/geo/CountryFlagIcon";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getCategoryProductSchema, listCategories, mockUploadFile } from "@/lib/catalog/mockCatalogApi";
import {
  PRODUCT_DOCS_ATTR,
  isAllowedProductDocFile,
  stripLocalFilesFromDraft,
} from "@/lib/catalog/productMedia";
import type {
  Attribute,
  AttributeGroup,
  AttributeOption,
  AttributeValue,
  Category,
  CategoryProductSchema,
  ProductDraft,
  UploadedFile,
} from "@/lib/catalog/types";
import { listBrandRecords, syncBrandsFromApi, type BrandRecord } from "@/lib/catalog/brandsStore";
import { listWarehouseRecords, syncWarehousesFromApi, type WarehouseRecord } from "@/lib/catalog/warehousesStore";
import {
  clearAddProductWizard,
  loadAddProductWizard,
  saveAddProductWizard,
} from "@/lib/catalog/addProductWizardStore";
import {
  catalogProductErrorMessage,
  deleteProduct,
  fetchProductFromApi,
  finalizeWizardProduct,
  getProductById,
  persistProductWizardProgress,
  syncProductsFromApi,
  updateProduct,
} from "@/lib/catalog/productsStore";
import { attributeScalarToOptionString } from "@/lib/catalog/attributeScalarString";
import { mergeAttributeOptionsInSchema } from "@/lib/catalog/attributeOptionsApi";
import {
  filterVisibleAttributes,
  flattenSchemaAttributes,
  isAttributeVisible,
  pruneHiddenAttributeValues,
  validateVisibleRequiredAttributes,
} from "@/lib/catalog/attributeVisibility";
import { PaidBySelect } from "@/components/catalog/PaidBySelect";
import { CurrencySelect } from "@/components/catalog/CurrencySelect";
import { DocumentTypeSelect } from "@/components/catalog/DocumentTypeSelect";
import { PurchaseCostTypeSelect } from "@/components/catalog/PurchaseCostTypeSelect";
import { ImportCountrySelect } from "@/components/catalog/ImportCountrySelect";
import { PartySelect } from "@/components/catalog/PartySelect";
import {
  attachNormalizedPurchaseFields,
  buildPaidByOptions,
  computeLandedCost,
  defaultPaidByPartyId,
  paidBySelectValue,
  parsePaidBySelectValue,
  sumPurchaseCostLines,
} from "@/lib/catalog/purchaseCost";
import {
  createPurchaseCostType,
  listPurchaseCostTypeRecords,
  syncPurchaseCostTypesFromApi,
} from "@/lib/catalog/purchaseCostTypesStore";
import {
  listPurchaseCostCategories,
  syncPurchaseCostCategoriesFromApi,
} from "@/lib/catalog/purchaseCostCategoriesStore";
import {
  createDocumentType,
  listDocumentTypeRecords,
  syncDocumentTypesFromApi,
} from "@/lib/catalog/documentTypesStore";
import {
  DEAL_TYPE_OPTIONS,
  applyOwnerVehicleSurplusPartnerDefaults,
  applyPartnerInvestmentAmountEdit,
  applyRemainingProfitPercentToPartners,
  clearedDealDependents,
  companyOwnerDealPartnerRow,
  emptyDealPartnerRow,
  emptyShowroomCharge,
  investmentAmountTotal,
  isOwnerVehicleSurplusDeal,
  isShowroomIndependentOfPartnerProfits,
  ownerVehicleSurplusPartnersMatch,
  partnerProfitsMatchRemaining,
  remainingInvestmentPool,
  showroomCommissionPreviewBase,
  validateDealDraft,
  withRecalculatedInvestmentRatios,
  type DealPartnerDraft,
  type ProductDealType,
} from "@/lib/catalog/dealPartners";
import { listCompanyPartyOptions, listPartyOptions, syncPartiesFromApi } from "@/lib/parties/partiesStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { slugify } from "@/lib/slugify";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ArrowLeft,
  Camera,
  Check,
  ChevronDown,
  ChevronLeft,
  Edit,
  FileText,
  Image as ImageIcon,
  Layers,
  Loader2,
  Package,
  Plus,
  Save,
  Search,
  Tag,
  Trash2,
  Upload,
  Users,
  Wallet,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import { isAxiosError } from "axios";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { fetchCurrencies, formatCurrency, getActiveCompanyCurrency, getCachedCurrencies } from "@/lib/currencies/currenciesStore";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";

function formatMoneyAmount(n: number | null | undefined, currency?: string): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return formatCurrency(Number(n), currency);
}

function sortByOrder<T extends { sortOrder?: number }>(items: T[]) {
  return [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

const SectionHeader = ({
  title,
  subtitle,
  icon,
  right,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ComponentType<{ className?: string }>;
  right?: ReactNode;
}) => {
  const Icon = icon;
  return (
    <div className="flex items-center justify-between gap-3 mb-2.5">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10">
          {Icon ? <Icon className="h-4 w-4 text-primary" /> : <FileText className="h-4 w-4 text-primary" />}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <h3 className="font-display text-sm font-semibold leading-tight tracking-tight text-foreground">{title}</h3>
          {subtitle ? <p className="text-xs leading-snug text-muted-foreground">{subtitle}</p> : null}
        </div>
      </div>
      {right != null && right !== false ? <div className="shrink-0 flex items-center gap-2">{right}</div> : null}
    </div>
  );
};

const ADDITIONAL_SELECT_TRIGGER =
  "h-10 w-full rounded-md border border-emerald-200/80 bg-emerald-50/90 px-3 text-sm text-foreground shadow-sm ring-offset-background focus:ring-2 focus:ring-ring/40 dark:border-emerald-800/50 dark:bg-emerald-950/35 dark:text-emerald-50/95";

const ADDITIONAL_TEXT_INPUT =
  "h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30";

function AdditionalInfoFieldRow({
  labelText,
  required,
  metaRight,
  alignTop,
  labelAction,
  children,
}: {
  labelText: string;
  required?: boolean;
  metaRight?: ReactNode;
  alignTop?: boolean;
  labelAction?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-2 py-4 sm:grid-cols-[minmax(10rem,15rem)_minmax(0,1fr)] sm:gap-x-10 sm:gap-y-0",
        alignTop ? "sm:items-start" : "sm:items-center",
      )}
    >
      <div className={cn("text-sm text-foreground sm:text-right", alignTop && "sm:pt-2.5")}>
        <div className="flex items-center justify-between gap-2 sm:justify-end">
          <span className="font-medium text-foreground/95">
            {labelText}
            {required ? <span className="text-destructive"> *</span> : null}
          </span>
          {labelAction}
        </div>
        {metaRight ? <div className="mt-1 text-left text-[11px] text-muted-foreground sm:text-right">{metaRight}</div> : null}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function AttributeField({
  attribute,
  value,
  onChange,
  layout = "default",
  suppressMultiselectLabel = false,
  featureGridCell = false,
  onAttributeOptionsUpdated,
}: {
  attribute: Attribute;
  value: AttributeValue | undefined;
  onChange: (v: AttributeValue) => void;
  layout?: "default" | "additional";
  /** Under the shared “Features” heading: show only the checkbox grid (no left label). */
  suppressMultiselectLabel?: boolean;
  /** Compact Yes/No tile inside the Features grid (5 columns). */
  featureGridCell?: boolean;
  onAttributeOptionsUpdated?: (attributeId: string, options: AttributeOption[]) => void;
}) {
  const [runtimeOptionOpen, setRuntimeOptionOpen] = useState(false);
  const label = (
    <div className="flex items-center justify-between gap-3">
      <Label className="text-xs text-muted-foreground">
        {attribute.name}
        {attribute.isRequired ? <span className="text-destructive"> *</span> : null}
      </Label>
      {attribute.type === "file" || attribute.type === "files" || attribute.type === "image" || attribute.type === "images" ? (
        <span className="text-[11px] text-muted-foreground/70">
          {attribute.maxSizeMb ? `Max ${attribute.maxSizeMb}MB` : null}
          {attribute.maxSizeMb && attribute.maxFiles ? " • " : null}
          {attribute.maxFiles ? `Up to ${attribute.maxFiles}` : null}
        </span>
      ) : null}
    </div>
  );

  const fileMeta =
    attribute.type === "file" || attribute.type === "files" || attribute.type === "image" || attribute.type === "images" ? (
      <span className="text-[11px] text-muted-foreground/80">
        {attribute.maxSizeMb ? `Max ${attribute.maxSizeMb}MB` : null}
        {attribute.maxSizeMb && attribute.maxFiles ? " · " : null}
        {attribute.maxFiles ? `Up to ${attribute.maxFiles} files` : null}
      </span>
    ) : null;

    if (layout === "additional") {
    if (attribute.type === "textarea") {
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired} alignTop>
          <Textarea
            className="min-h-[120px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${attribute.name.toLowerCase()}`}
          />
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "boolean") {
      if (featureGridCell) {
        return (
          <label className="flex min-h-0 cursor-pointer items-center gap-2.5 rounded-md border border-border/55 bg-background/90 p-2.5 shadow-sm">
            <Checkbox
              checked={Boolean(value)}
              onCheckedChange={(checked) => onChange(Boolean(checked))}
              className="shrink-0 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
            <span className="min-w-0 flex-1 text-[0.8125rem] font-medium leading-snug text-foreground">
              {attribute.name}
              {attribute.isRequired ? <span className="text-destructive"> *</span> : null}
            </span>
          </label>
        );
      }
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired}>
          <label className="flex cursor-pointer items-center gap-2.5 rounded-md py-1 text-sm text-foreground">
            <Checkbox
              checked={Boolean(value)}
              onCheckedChange={(checked) => onChange(Boolean(checked))}
              className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
          </label>
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "select") {
      return (
        <AdditionalInfoFieldRow
          labelText={attribute.name}
          required={attribute.isRequired}
          labelAction={
            <AttributeSelectRuntimeAddButton attribute={attribute} onClick={() => setRuntimeOptionOpen(true)} />
          }
        >
          <AttributeSelectControl
            attribute={attribute}
            value={value}
            onChange={onChange}
            onOptionsUpdated={onAttributeOptionsUpdated}
            triggerClassName={ADDITIONAL_SELECT_TRIGGER}
            placeholder={attribute.name}
            runtimeOpen={runtimeOptionOpen}
            onRuntimeOpenChange={setRuntimeOptionOpen}
          />
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "multiselect") {
      const selected = Array.isArray(value)
        ? (value as unknown[]).map((v) => attributeScalarToOptionString(v as AttributeValue))
        : [];
      const options = sortByOrder(attribute.options ?? []);
      const grid = (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 md:gap-x-2 md:gap-y-2">
          {options.map((opt) => {
            const key = String(opt.value ?? "");
            if (!key) return null;
            const checked = selected.includes(key);
            return (
              <label
                key={opt.id}
                className="flex min-w-0 cursor-pointer items-center gap-2 py-1 text-xs leading-snug text-foreground/90 transition-colors hover:text-foreground sm:text-[0.8125rem]"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(next) => {
                    const nextChecked = Boolean(next);
                    const nextValues = nextChecked ? [...selected, key] : selected.filter((v) => v !== key);
                    onChange(nextValues);
                  }}
                  className="shrink-0 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <span className="min-w-0 truncate sm:whitespace-normal sm:break-words">{opt.label}</span>
              </label>
            );
          })}
        </div>
      );
      if (suppressMultiselectLabel) {
        return <div className="py-3 sm:py-4">{grid}</div>;
      }
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired} alignTop>
          {grid}
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "date") {
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired}>
          <Input
            type="date"
            className={ADDITIONAL_TEXT_INPUT}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "number") {
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired}>
          <Input
            type="number"
            className={ADDITIONAL_TEXT_INPUT}
            value={typeof value === "number" ? String(value) : typeof value === "string" ? value : ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") return onChange(null);
              const n = Number(raw);
              onChange(Number.isFinite(n) ? n : null);
            }}
            placeholder={attribute.name}
          />
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "image" || attribute.type === "file") {
      const current = value as UploadedFile | undefined;
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired} metaRight={fileMeta ?? undefined}>
          <div className="flex flex-wrap items-start gap-3">
            <Input
              type="file"
              className={cn(ADDITIONAL_TEXT_INPUT, "h-auto min-h-10 cursor-pointer py-2")}
              accept={attribute.accept}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const uploaded = await mockUploadFile(file, attribute);
                onChange(uploaded);
              }}
            />
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border/50 bg-muted/30">
              {attribute.type === "image" ? <ImageIcon className="h-5 w-5 text-muted-foreground" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
            </div>
          </div>
          {attribute.type === "image" && current?.url ? (
            <div className="mt-3 max-w-xs overflow-hidden rounded-md border border-border/40">
              <img src={current.url} alt={current.filename} className="h-auto w-full object-cover" />
            </div>
          ) : null}
          {current ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Selected: <span className="text-foreground/90">{current.filename}</span>
            </p>
          ) : null}
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "images" || attribute.type === "files") {
      const current = Array.isArray(value) ? (value as UploadedFile[]) : [];
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired} metaRight={fileMeta ?? undefined} alignTop>
          <Input
            type="file"
            multiple
            className={cn(ADDITIONAL_TEXT_INPUT, "h-auto min-h-10 cursor-pointer py-2")}
            accept={attribute.accept}
            onChange={async (e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length === 0) return;
              const uploads: UploadedFile[] = [];
              for (const f of files) {
                uploads.push(await mockUploadFile(f, attribute));
              }
              const max = attribute.maxFiles ?? uploads.length;
              onChange(uploads.slice(0, max));
            }}
          />
          {current.length ? (
            <div className="mt-2 space-y-2 text-xs text-muted-foreground">
              <p>
                Selected {current.length} file{current.length === 1 ? "" : "s"}.
              </p>
              {attribute.type === "images" ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {current
                    .filter((u) => Boolean(u.url))
                    .map((u) => (
                      <div key={u.id} className="aspect-video overflow-hidden rounded-md border border-border/40 bg-muted/20">
                        <img src={u.url} alt={u.filename} className="h-full w-full object-cover" />
                      </div>
                    ))}
                </div>
              ) : (
                <ul className="list-disc pl-5">
                  {current.map((u) => (
                    <li key={u.id}>
                      <span className="text-foreground/90">{u.filename}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </AdditionalInfoFieldRow>
      );
    }
    return (
      <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired}>
        <Input
          className={ADDITIONAL_TEXT_INPUT}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={attribute.name}
        />
      </AdditionalInfoFieldRow>
    );
  }

  if (attribute.type === "textarea") {
    return (
      <div className="space-y-2">
        {label}
        <Textarea
          className="glass-input min-h-[96px]"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Enter ${attribute.name.toLowerCase()}`}
        />
      </div>
    );
  }

  if (attribute.type === "boolean") {
    return (
      <label className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors p-2 rounded-lg hover:bg-secondary/30">
        <Checkbox
          checked={Boolean(value)}
          onCheckedChange={(checked) => onChange(Boolean(checked))}
          className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
        />
        <span>
          {attribute.name}
          {attribute.isRequired ? <span className="text-destructive"> *</span> : null}
        </span>
      </label>
    );
  }

  if (attribute.type === "select") {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <Label className="text-xs text-muted-foreground">
            {attribute.name}
            {attribute.isRequired ? <span className="text-destructive"> *</span> : null}
          </Label>
          <AttributeSelectRuntimeAddButton attribute={attribute} onClick={() => setRuntimeOptionOpen(true)} />
        </div>
        <AttributeSelectControl
          attribute={attribute}
          value={value}
          onChange={onChange}
          onOptionsUpdated={onAttributeOptionsUpdated}
          placeholder={`Select ${attribute.name.toLowerCase()}`}
          runtimeOpen={runtimeOptionOpen}
          onRuntimeOpenChange={setRuntimeOptionOpen}
        />
      </div>
    );
  }

  if (attribute.type === "multiselect") {
    const selected = Array.isArray(value)
      ? (value as unknown[]).map((v) => attributeScalarToOptionString(v as AttributeValue))
      : [];
    const options = sortByOrder(attribute.options ?? []);
    return (
      <div className="space-y-2">
        {label}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {options.map((opt) => {
            const key = String(opt.value ?? "");
            if (!key) return null;
            const checked = selected.includes(key);
            return (
              <label
                key={opt.id}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors p-2 rounded-lg hover:bg-secondary/30"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(next) => {
                    const nextChecked = Boolean(next);
                    const nextValues = nextChecked ? [...selected, key] : selected.filter((v) => v !== key);
                    onChange(nextValues);
                  }}
                  className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  if (attribute.type === "date") {
    return (
      <div className="space-y-2">
        {label}
        <Input
          type="date"
          className="glass-input h-10"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  if (attribute.type === "number") {
    return (
      <div className="space-y-2">
        {label}
        <Input
          type="number"
          className="glass-input h-10"
          value={typeof value === "number" ? String(value) : typeof value === "string" ? value : ""}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return onChange(null);
            const n = Number(raw);
            onChange(Number.isFinite(n) ? n : null);
          }}
          placeholder={`Enter ${attribute.name.toLowerCase()}`}
        />
      </div>
    );
  }

  if (attribute.type === "image" || attribute.type === "file") {
    const current = value as UploadedFile | undefined;
    return (
      <div className="space-y-2">
        {label}
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <Input
              type="file"
              className="glass-input h-10 pt-2"
              accept={attribute.accept}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const uploaded = await mockUploadFile(file, attribute);
                onChange(uploaded);
              }}
            />
            {current ? (
              <p className="text-xs text-muted-foreground mt-2">
                Selected: <span className="text-foreground/90">{current.filename}</span>
              </p>
            ) : null}
          </div>
          <div className="w-12 h-12 rounded-lg border border-border/40 bg-secondary/20 flex items-center justify-center">
            {attribute.type === "image" ? <ImageIcon className="w-5 h-5 text-muted-foreground" /> : <Upload className="w-5 h-5 text-muted-foreground" />}
          </div>
        </div>
        {attribute.type === "image" && current?.url ? (
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="aspect-video rounded-lg overflow-hidden border border-border/40 bg-secondary/20">
              <img src={current.url} alt={current.filename} className="w-full h-full object-cover" />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (attribute.type === "images" || attribute.type === "files") {
    const current = Array.isArray(value) ? (value as UploadedFile[]) : [];
    return (
      <div className="space-y-2">
        {label}
        <Input
          type="file"
          multiple
          className="glass-input h-10 pt-2"
          accept={attribute.accept}
          onChange={async (e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length === 0) return;
            const uploads: UploadedFile[] = [];
            for (const f of files) {
              uploads.push(await mockUploadFile(f, attribute));
            }
            const max = attribute.maxFiles ?? uploads.length;
            onChange(uploads.slice(0, max));
          }}
        />
        {current.length ? (
          <div className="mt-2 space-y-2">
            <p className="text-xs text-muted-foreground">
              Selected {current.length} file{current.length === 1 ? "" : "s"}.
            </p>
            {attribute.type === "images" ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {current
                  .filter((u) => Boolean(u.url))
                  .map((u) => (
                    <div key={u.id} className="aspect-video rounded-lg overflow-hidden border border-border/40 bg-secondary/20">
                      <img src={u.url} alt={u.filename} className="w-full h-full object-cover" />
                    </div>
                  ))}
              </div>
            ) : (
              <ul className="text-xs text-muted-foreground list-disc pl-5">
                {current.map((u) => (
                  <li key={u.id}>
                    <span className="text-foreground/90">{u.filename}</span>
                    <span className="text-muted-foreground/70"> ({Math.round(u.size / 1024)} KB)</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {label}
      <Input
        className="glass-input h-10"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${attribute.name.toLowerCase()}`}
      />
    </div>
  );
}

/** Category schema group shown on Basic information (after status), not duplicated on the Additional information step. */
function isBasicSpecsAttributeGroup(group: AttributeGroup): boolean {
  const slug = (group.slug ?? "").toLowerCase();
  const name = (group.name ?? "").toLowerCase();
  if (slug === "basic-specs" || slug === "basic-spec") return true;
  if (name.includes("basic spec") || name.includes("basic details")) return true;
  return false;
}

/** “Basic details” group: no bordered/muted shell on basic info (matches plain grid fields). */
function isBasicDetailsAttributeGroup(group: AttributeGroup): boolean {
  const slug = (group.slug ?? "").toLowerCase();
  const name = (group.name ?? "").toLowerCase();
  if (slug === "basic-details" || slug === "basic-detail") return true;
  return name.includes("basic detail");
}

/** Shown under Additional information (after Transmission), not on Basic. */
function isAssemblyAttribute(a: Attribute): boolean {
  const n = (a.name ?? "").trim().toLowerCase();
  const c = (a.code ?? "").trim().toLowerCase().replace(/-/g, "_");
  return n === "assembly" || c === "assembly" || c.endsWith("_assembly");
}

function collectAssemblyAttributes(allGroups: AttributeGroup[]): Attribute[] {
  const out: Attribute[] = [];
  const seen = new Set<string>();
  for (const g of allGroups) {
    for (const a of sortByOrder(g.attributes)) {
      if (!isAssemblyAttribute(a) || seen.has(a.id)) continue;
      seen.add(a.id);
      out.push(a);
    }
  }
  return out;
}

function filterBasicSpecGridAttributes(attrs: Attribute[]): Attribute[] {
  return sortByOrder(attrs).filter((a) => !isAssemblyAttribute(a));
}

function validateAdditionalInformationStep(
  d: ProductDraft,
  specOnlyGroups: AttributeGroup[],
  assemblyAttrs: Attribute[],
  allAttrs: Attribute[],
): boolean {
  if (
    !validateVisibleRequiredAttributes(d, specOnlyGroups, (msg) => toast.error(msg), (a) => isAssemblyAttribute(a))
  ) {
    return false;
  }
  for (const attr of assemblyAttrs) {
    if (!isAttributeVisible(attr, allAttrs, d.attributes)) continue;
    if (!attr.isRequired) continue;
    const value = d.attributes[attr.code];
    const missing =
      value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);
    if (missing) {
      toast.error(`Required field: ${attr.name}`);
      return false;
    }
  }
  return true;
}

/** Engine Type → Engine Capacity → Transmission → Assembly, then any other fields (original schema order). */
function sortAdditionalMainAttrs(attrs: Attribute[], stableOrder: Map<string, number>): Attribute[] {
  const norm = (s: string) => s.trim().toLowerCase().replace(/-/g, "_").replace(/\s+/g, " ");

  const matchers: ((a: Attribute) => boolean)[] = [
    (a) => {
      const n = norm(a.name);
      const c = norm(a.code ?? "");
      return (
        n === "engine type" ||
        (n.includes("engine") && n.includes("type") && !n.includes("capacity")) ||
        c.includes("engine_type") ||
        c === "engine_type" ||
        c === "enginetype"
      );
    },
    (a) => {
      const n = norm(a.name);
      const c = norm(a.code ?? "");
      return (
        (n.includes("engine") && n.includes("capacity")) ||
        n.includes("(cc)") ||
        c.includes("engine_capacity") ||
        c.includes("enginecapacity") ||
        (c.includes("displacement") && !c.includes("type"))
      );
    },
    (a) => {
      const n = norm(a.name);
      const c = norm(a.code ?? "");
      return n.includes("transmission") || c.includes("transmission");
    },
    (a) => {
      const n = norm(a.name);
      const c = norm(a.code ?? "");
      return n.includes("assembly") || c.includes("assembly") || c.includes("assembled");
    },
  ];

  const rank = (a: Attribute): number => {
    for (let i = 0; i < matchers.length; i++) {
      if (matchers[i](a)) return i;
    }
    return matchers.length;
  };

  return [...attrs].sort((a, b) => {
    const d = rank(a) - rank(b);
    if (d !== 0) return d;
    return (stableOrder.get(a.id) ?? 0) - (stableOrder.get(b.id) ?? 0);
  });
}

/** Preferred order for vehicle Yes/No feature booleans (normalized labels). */
const FEATURE_BOOLEAN_ORDER: string[] = [
  "sun roof",
  "panoramic sunroof",
  "drls",
  "led headlights",
  "front fog lights",
  "alloy wheels",
  "front speakers",
  "rear speakers",
  "paddle shifters",
  "infotainment system",
  "hud",
  "android auto",
  "apple carplay",
  "speakers",
  "steering switches",
  "parking sensors",
  "rear camera",
  "front camera",
  "360 camera",
  "abs",
  "airbags",
  "air bags",
  "traction control",
  "tpms",
  "cameras",
  "immobilizer",
  "power locks",
  "power mirrors",
  "power steering",
  "rear ac vents",
  "ventilated seats",
  "power seats",
  "heated seats",
  "climate control",
  "cruise control",
  "push start",
  "keyless entry",
  "ac",
];

function normFeatureLabel(s: string): string {
  return s.trim().toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ").replace(/\s*\(\s*/g, " ").replace(/\s*\)\s*/g, " ").trim();
}

function featureBooleanSortRank(a: Attribute, stableOrder: Map<string, number>): number {
  const n = normFeatureLabel(a.name);
  const c = (a.code ?? "").trim().toLowerCase().replace(/[-\s]+/g, "_");
  for (let i = 0; i < FEATURE_BOOLEAN_ORDER.length; i++) {
    const key = FEATURE_BOOLEAN_ORDER[i];
    const keySnake = key.replace(/\s/g, "_");
    const keyCompact = key.replace(/\s/g, "");
    if (n === key || c === keySnake || c === keyCompact) return i;
  }
  for (let i = 0; i < FEATURE_BOOLEAN_ORDER.length; i++) {
    const key = FEATURE_BOOLEAN_ORDER[i];
    if (key.length < 3) continue;
    if (n.includes(key)) return i;
  }
  return FEATURE_BOOLEAN_ORDER.length + (stableOrder.get(a.id) ?? 0);
}

function sortFeatureBooleanAttrs(attrs: Attribute[], stableOrder: Map<string, number>): Attribute[] {
  return [...attrs].sort((a, b) => {
    const d = featureBooleanSortRank(a, stableOrder) - featureBooleanSortRank(b, stableOrder);
    if (d !== 0) return d;
    return (stableOrder.get(a.id) ?? 0) - (stableOrder.get(b.id) ?? 0);
  });
}

function savePayload(draft: ProductDraft): ProductDraft {
  const showroomBase =
    draft.showroomCharge ?? draft.companyOwnerCommission ?? emptyShowroomCharge();
  const companyParties = listCompanyPartyOptions();
  const fallbackParty = companyParties[0] ?? null;
  const showroomCharge = emptyShowroomCharge({
    ...showroomBase,
    partyId: showroomBase.partyId ?? fallbackParty?.id ?? null,
    partyName: showroomBase.partyName ?? fallbackParty?.name ?? null,
  });

  const isConsignment = draft.acquisitionType === "consignment";

  return attachNormalizedPurchaseFields({
    ...draft,
    name: draft.name.trim(),
    slug: (draft.slug?.trim() ? slugify(draft.slug) : slugify(draft.name)) || slugify(draft.name),
    brandId: draft.brandId || null,
    supplierId: isConsignment ? null : (draft.supplierId || null),
    purchaseType: isConsignment ? null : (draft.purchaseType === "import" ? "import" : "local"),
    importCountryId: !isConsignment && draft.purchaseType === "import" ? draft.importCountryId ?? null : null,
    purchasePrice: isConsignment ? null : (draft.purchasePrice === null || draft.purchasePrice === undefined ? null : Number(draft.purchasePrice)),
    purchaseCurrency: isConsignment ? null : (draft.purchaseCurrency || getActiveCompanyCurrency().code),
    purchaseCurrencyAmount: isConsignment ? null : (draft.purchaseCurrencyAmount ?? null),
    purchaseExchangeRate: isConsignment ? null : (draft.purchaseExchangeRate ?? null),
    purchaseNotes: draft.purchaseNotes?.trim() || null,
    productImages: draft.productImages ?? [],
    productDocuments: draft.productDocuments ?? [],
    documentType: draft.documentType === "duplicate" ? "duplicate" : "original",
    numberOfDocuments: (() => {
      const n = Number(draft.numberOfDocuments ?? 0);
      if (!Number.isInteger(n) || n < 0) return 0;
      return Math.min(100, n);
    })(),
    purchaseCostLines: (draft.purchaseCostLines ?? []).map((line) => {
      const dealType = isConsignment ? "owner_vehicle" : (draft.dealType ?? "purchased");
      let costBearerPartyId: string | number | null = "partnership";
      if (dealType === "purchased") {
        costBearerPartyId = fallbackParty?.id ?? null;
      } else if (dealType === "owner_vehicle") {
        costBearerPartyId = draft.dealPartners?.[0]?.partyId ? Number(draft.dealPartners[0].partyId) : null;
      }
      return {
        ...line,
        costBearerPartyId,
      };
    }),
    salePrice: draft.salePrice === null || draft.salePrice === undefined ? null : Number(draft.salePrice),
    expectedSalePrice:
      draft.expectedSalePrice === null || draft.expectedSalePrice === undefined
        ? null
        : Number(draft.expectedSalePrice),
    ownerDemandPrice:
      draft.ownerDemandPrice === null || draft.ownerDemandPrice === undefined
        ? null
        : Number(draft.ownerDemandPrice),
    dealType: draft.dealType ?? null,
    profitRuleType: draft.profitRuleType ?? null,
    dealPartners: draft.dealPartners ?? [],
    showroomCharge,
    companyOwnerCommission: showroomCharge,
  });
}

const BASE_WIZARD_STEPS = [
  { id: "basic", label: "Basic information", short: "Basics", icon: Package },
  { id: "spec", label: "Additional Information", short: "Additional", icon: Layers },
  { id: "media", label: "Images / documents", short: "Media", icon: Camera },
  { id: "purchase", label: "Purchase & landed cost", short: "Purchase", icon: Wallet },
] as const;

function defaultProductDraft(): ProductDraft {
  return {
    categoryId: "",
    brandId: null,
    vehicleModelId: null,
    vehicleVersionId: null,
    modelYear: null,
    supplierId: null,
    acquisitionType: "purchased",
    purchaseType: "local",
    importCountryId: null,
    warehouseId: null,
    name: "",
    slug: "",
    description: "",
    inventoryStatus: "draft",
    purchasePrice: null,
    purchaseCurrency: getActiveCompanyCurrency().code,
    purchaseCurrencyAmount: null,
    purchaseExchangeRate: 1,
    purchaseNotes: "",
    productImages: [],
    productDocuments: [],
    documentCategory: null,
    documentCategoryId: null,
    documentType: "original",
    numberOfDocuments: 0,
    purchaseCostLines: [],
    landedCost: null,
    salePrice: null,
    dealType: null,
    profitRuleType: null,
    expectedSalePrice: null,
    ownerDemandPrice: null,
    dealPartners: [],
    companyOwnerCommission: companyOwnerDealPartnerRow(),
    showroomCharge: emptyShowroomCharge(),
    attributes: {},
  };
}

function mergeWizardDraft(saved: ProductDraft): ProductDraft {
  const raw = saved as ProductDraft & { additionalNotes?: unknown };
  const { additionalNotes: _omit, ...rest } = raw;
  const base = defaultProductDraft();
  return {
    ...base,
    ...rest,
    purchaseType: rest.purchaseType === "import" ? "import" : "local",
    importCountryId: rest.importCountryId ?? null,
    vehicleModelId: rest.vehicleModelId ?? base.vehicleModelId ?? null,
    vehicleVersionId: rest.vehicleVersionId ?? base.vehicleVersionId ?? null,
    modelYear: rest.modelYear ?? base.modelYear ?? null,
    warehouseId: rest.warehouseId ?? base.warehouseId ?? null,
    inventoryStatus: rest.inventoryStatus ?? base.inventoryStatus ?? "draft",
    attributes: { ...base.attributes, ...(rest.attributes ?? {}) },
    productImages: Array.isArray(rest.productImages) ? rest.productImages : [],
    productDocuments: Array.isArray(rest.productDocuments) ? rest.productDocuments : [],
    documentCategory: rest.documentCategory ?? null,
    documentCategoryId: rest.documentCategoryId ?? null,
    documentType: rest.documentType === "duplicate" ? "duplicate" : "original",
    numberOfDocuments: (() => {
      const n = Number(rest.numberOfDocuments ?? 0);
      if (!Number.isInteger(n) || n < 0) return 0;
      return Math.min(100, n);
    })(),
    purchaseCostLines: Array.isArray(rest.purchaseCostLines) ? rest.purchaseCostLines : [],
    dealPartners: Array.isArray(rest.dealPartners) ? rest.dealPartners : [],
    companyOwnerCommission: rest.showroomCharge ?? rest.companyOwnerCommission ?? emptyShowroomCharge(),
    showroomCharge: rest.showroomCharge ?? rest.companyOwnerCommission ?? emptyShowroomCharge(),
  };
}

function validateBasicStep(d: ProductDraft): boolean {
  if (!d.categoryId?.trim()) {
    toast.error("Select a category to continue.");
    return false;
  }
  if (!d.name?.trim()) {
    toast.error("Enter a product name.");
    return false;
  }
  if (!d.slug?.trim() && !slugify(d.name)) {
    toast.error("Enter a URL slug.");
    return false;
  }
  return true;
}

function validateDocumentMeta(d: ProductDraft): boolean {
  const docs = d.productDocuments ?? [];
  if (!docs.length) return true;
  for (const doc of docs) {
    const copy = doc.documentCopy ?? d.documentType;
    if (copy !== "original" && copy !== "duplicate") {
      toast.error(`Select a document copy for “${doc.filename}”.`);
      return false;
    }
    const n = Number(doc.numberOfPages ?? 0);
    if (!Number.isInteger(n) || n < 0 || n > 100) {
      toast.error(`No. of pages for “${doc.filename}” must be a whole number from 0 to 100.`);
      return false;
    }
  }
  return true;
}

const AddProduct = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const isEdit = Boolean(routeId);
  const [authUser, setAuthUser] = useState(() => getStoredAuthUser());
  const canAccessEditor = isEdit ? can(authUser, "product_can_update") : can(authUser, "product_can_create");
  const canViewPrices = can(authUser, "product_can_view_prices");
  const canCreatePurchaseCostType = can(authUser, "purchase_cost_type_can_create");
  const canCreateInvestor = can(authUser, "investor_can_create");
  const canCreateDocumentType =
    can(authUser, "document_type_can_create") ||
    can(authUser, "product_can_create") ||
    can(authUser, "product_can_update");

  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<BrandRecord[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [parties, setParties] = useState(() => listPartyOptions());
  const [partnerSearch, setPartnerSearch] = useState("");
  const [addInvestorOpen, setAddInvestorOpen] = useState(false);

  useEffect(() => {
    void fetchCountries().then(setCountries).catch(() => setCountries([]));
  }, []);
  const [companyParties, setCompanyParties] = useState(() => listCompanyPartyOptions());
  const [schema, setSchema] = useState<CategoryProductSchema | null>(null);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [purchaseCostTypesVersion, setPurchaseCostTypesVersion] = useState(0);
  const [purchaseCostCategoriesVersion, setPurchaseCostCategoriesVersion] = useState(0);
  const [documentTypesVersion, setDocumentTypesVersion] = useState(0);
  const [costTypeDialogOpen, setCostTypeDialogOpen] = useState(false);
  const [costTypeDialogLineIndex, setCostTypeDialogLineIndex] = useState<number | null>(null);
  const [costTypeLabel, setCostTypeLabel] = useState("");
  const [costTypeCategoryId, setCostTypeCategoryId] = useState("");
  const [costTypeSaving, setCostTypeSaving] = useState(false);
  const [documentTypeDialogOpen, setDocumentTypeDialogOpen] = useState(false);
  const [documentTypeName, setDocumentTypeName] = useState("");
  const [documentTypeSaving, setDocumentTypeSaving] = useState(false);

  const [wizardStep, setWizardStep] = useState(0);
  const wizardSteps = useMemo(
    () => BASE_WIZARD_STEPS.filter((s) => canViewPrices || s.id !== "purchase"),
    [canViewPrices],
  );
  const stepCount = wizardSteps.length;
  const currentStepId = wizardSteps[wizardStep]?.id ?? "basic";
  const [draft, setDraft] = useState<ProductDraft>(() => defaultProductDraft());
  const [persistedProductId, setPersistedProductId] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [pendingCostLineRemoveIndex, setPendingCostLineRemoveIndex] = useState<number | null>(null);

  // Additional Cost Line Modal state
  const [costLineModalOpen, setCostLineModalOpen] = useState(false);
  const [editingCostLineIndex, setEditingCostLineIndex] = useState<number | null>(null);
  const [costLineModalForm, setCostLineModalForm] = useState<PurchaseCostLine>(() => ({
    typeId: "",
    amount: 0,
    currencyCode: "PKR",
    currencyAmount: 0,
    exchangeRate: 1,
    paidByPartyId: null,
    paidToPartyId: null,
    costBearerPartyId: "partnership",
    paymentMethod: "cash",
    bankName: null,
    transactionReferenceNo: null,
    payOrderNo: null,
    description: null,
  }));
  /** Edit mode: wait for fresh product from API so category + attributes match server (local cache can be stale). */
  const [editReady, setEditReady] = useState(!isEdit);
  const wizardBootstrapped = useRef(false);
  const persistedIdRef = useRef<string | null>(null);
  /** Tracks Base Purchase Cost last used to auto-fill Owner Demand Price. */
  const basePurchaseForOwnerDemandRef = useRef<number | null>(null);

  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    persistedIdRef.current = persistedProductId;
  }, [persistedProductId]);

  useEffect(() => {
    setWizardStep((s) => Math.min(s, Math.max(0, stepCount - 1)));
  }, [stepCount]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setAuthUser(getStoredAuthUser());
    };
    const onAuth = () => setAuthUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!canAccessEditor) {
      toast.error("User does not have the right permissions.");
      navigate("/dashboard", { replace: true });
    }
  }, [canAccessEditor, navigate]);

  // New product: always start empty. Do not restore add-product session draft (it showed the previous
  // car’s fields when opening /products/add again in the same browser session).
  useEffect(() => {
    if (isEdit) return;
    if (wizardBootstrapped.current) return;
    wizardBootstrapped.current = true;

    clearAddProductWizard();
    setWizardStep(0);
    setDraft(defaultProductDraft());
    setPersistedProductId(null);
    persistedIdRef.current = null;
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit || !routeId) {
      if (!isEdit) setEditReady(true);
      return;
    }

    let cancelled = false;
    setEditReady(false);

    (async () => {
      let p = getProductById(routeId);
      try {
        const fromApi = await fetchProductFromApi(routeId);
        if (!cancelled && fromApi) p = fromApi;
      } catch (error) {
        if (!(isAxiosError(error) && error.response?.status === 403)) {
          console.error("[AddProduct] Failed to load product for edit", error);
        }
      }

      if (cancelled) return;

      const categoryId = p?.categoryId?.trim() ?? "";
      if (!p || !categoryId) {
        toast.error("Product not found.");
        navigate("/products", { replace: true });
        return;
      }

      if (!wizardBootstrapped.current) wizardBootstrapped.current = true;
      setWizardStep(0);
      setPersistedProductId(routeId);
      persistedIdRef.current = routeId;
      setDraft(
        mergeWizardDraft({
          categoryId: p.categoryId,
          brandId: p.brandId ?? null,
          vehicleModelId: p.vehicleModelId ?? null,
          vehicleVersionId: p.vehicleVersionId ?? null,
          modelYear: p.modelYear ?? null,
          supplierId: p.supplierId ?? null,
          acquisitionType: p.acquisitionType ?? "purchased",
          purchaseType: p.purchaseType ?? "local",
          importCountryId: p.importCountryId ?? null,
          warehouseId: p.warehouseId ?? null,
          name: p.name,
          slug: p.slug,
          description: p.description ?? "",
          purchasePrice: p.purchasePrice ?? null,
          purchaseCurrency: p.purchaseCurrency ?? "PKR",
          purchaseCurrencyAmount: p.purchaseCurrencyAmount ?? null,
          purchaseExchangeRate: p.purchaseExchangeRate ?? null,
          purchaseNotes: p.purchaseNotes ?? "",
          purchaseCostLines: p.purchaseCostLines ?? [],
          landedCost: p.landedCost ?? null,
          salePrice: p.salePrice ?? null,
          dealType: p.dealType ?? null,
          profitRuleType: p.profitRuleType ?? null,
          expectedSalePrice: p.expectedSalePrice ?? null,
          ownerDemandPrice: p.ownerDemandPrice ?? null,
          dealPartners: p.dealPartners ?? [],
          companyOwnerCommission: p.showroomCharge ?? p.companyOwnerCommission ?? emptyShowroomCharge(),
          showroomCharge: p.showroomCharge ?? p.companyOwnerCommission ?? emptyShowroomCharge(),
          productImages: p.productImages ?? [],
          productDocuments: p.productDocuments ?? [],
          documentCategory: p.documentCategory ?? null,
          documentCategoryId: p.documentCategoryId ?? null,
          documentType: p.documentType === "duplicate" ? "duplicate" : "original",
          numberOfDocuments: (() => {
            const n = Number(p.numberOfDocuments ?? 0);
            if (!Number.isInteger(n) || n < 0) return 0;
            return Math.min(100, n);
          })(),
          attributes: p.attributes ?? {},
          inventoryStatus: p.inventoryStatus,
        }),
      );
      setEditReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, routeId, navigate]);

  const docInputRef = useRef<HTMLInputElement>(null);
  const [docDropActive, setDocDropActive] = useState(false);
  const [vehiclePickerOpen, setVehiclePickerOpen] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>(getCachedCurrencies());

  useEffect(() => {
    let mounted = true;
    const loadCatalogRefs = async () => {
      try {
        const cats = await listCategories();
        if (mounted) setCategories(cats);
      } catch (error) {
        console.error("[AddProduct] Categories load failed", error);
        if (mounted) {
          setCategories([]);
          toast.error("Could not load categories.");
        }
      }
      try {
        await syncBrandsFromApi();
        if (mounted) setBrands(listBrandRecords());
      } catch (error) {
        console.error("[AddProduct] Brands load failed", error);
        if (mounted) {
          setBrands(listBrandRecords());
          toast.error("Could not load brands. Showing cached list if available.");
        }
      }
      try {
        await syncWarehousesFromApi({ activeOnly: true });
        if (mounted) setWarehouses(listWarehouseRecords());
      } catch (error) {
        if (!(isAxiosError(error) && error.response?.status === 403)) {
          console.error("[AddProduct] Locations load failed", error);
        }
        if (mounted) {
          setWarehouses(listWarehouseRecords());
        }
      }
      try {
        await syncPartiesFromApi();
        await syncPartiesFromApi({ isCompany: true });
        if (mounted) {
          setParties(listPartyOptions());
          setCompanyParties(listCompanyPartyOptions());
        }
      } catch (error) {
        console.error("[AddProduct] Parties load failed", error);
        if (mounted) {
          setParties(listPartyOptions());
          setCompanyParties(listCompanyPartyOptions());
        }
      }
      fetchCurrencies().then((rows) => { if (mounted) setCurrencies(rows); });
      try {
        await syncProductsFromApi();
        if (!isEdit && mounted) {
          const wid = persistedIdRef.current;
          if (wid && !getProductById(wid)) {
            persistedIdRef.current = null;
            setPersistedProductId(null);
            const snap = loadAddProductWizard();
            if (snap) {
              saveAddProductWizard({
                step: snap.step,
                draft: snap.draft,
              });
            }
          }
        }
      } catch (error) {
        if (!(isAxiosError(error) && error.response?.status === 403)) {
          console.error("[AddProduct] Products sync failed", error);
        }
      }
      try {
        await syncPurchaseCostCategoriesFromApi();
        if (mounted) setPurchaseCostCategoriesVersion((v) => v + 1);
      } catch (error) {
        console.error("[AddProduct] Purchase cost categories sync failed", error);
      }
      try {
        await syncPurchaseCostTypesFromApi();
        if (mounted) setPurchaseCostTypesVersion((v) => v + 1);
      } catch (error) {
        console.error("[AddProduct] Purchase cost types sync failed", error);
      }
      try {
        await syncDocumentTypesFromApi();
        if (mounted) setDocumentTypesVersion((v) => v + 1);
      } catch (error) {
        console.error("[AddProduct] Document types sync failed", error);
      }
    };
    void loadCatalogRefs();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!draft.categoryId) {
      setSchema(null);
      return;
    }
    setLoadingSchema(true);
    getCategoryProductSchema(draft.categoryId)
      .then((s) => setSchema(s))
      .finally(() => setLoadingSchema(false));
  }, [draft.categoryId]);

  const groups = useMemo(() => sortByOrder(schema?.groups ?? []), [schema]);
  const basicSpecGroups = useMemo(() => groups.filter(isBasicSpecsAttributeGroup), [groups]);
  const specOnlyGroups = useMemo(() => groups.filter((g) => !isBasicSpecsAttributeGroup(g)), [groups]);
  const allSchemaAttributes = useMemo(() => flattenSchemaAttributes(groups), [groups]);
  const assemblyAttrsList = useMemo(() => collectAssemblyAttributes(groups), [groups]);

  const updateProductAttribute = useCallback(
    (code: string, value: AttributeValue, attr?: Attribute) => {
      setDraft((d) => {
        let attributes = { ...d.attributes, [code]: value };
        if (attr && (attr.type === "select" || attr.type === "multiselect")) {
          attributes = pruneHiddenAttributeValues(attributes, groups);
        }
        return { ...d, attributes };
      });
    },
    [groups],
  );

  const handleAttributeOptionsUpdated = useCallback((attributeId: string, options: AttributeOption[]) => {
    setSchema((prev) => (prev ? mergeAttributeOptionsInSchema(prev, attributeId, options) : prev));
  }, []);

  /**
   * Main block: non-boolean fields from additional groups + Assembly (after Transmission order).
   * Features: boolean Yes/No attributes plus multiselect checklists.
   */
  const { additionalMainAttrs, additionalFeatureBooleans, additionalFeatureMultiselects } = useMemo(() => {
    const main: Attribute[] = [];
    const featureBool: Attribute[] = [];
    const featureMulti: Attribute[] = [];
    const stableOrderMain = new Map<string, number>();
    const stableOrderFeature = new Map<string, number>();
    let posMain = 0;
    let posFeat = 0;
    for (const g of specOnlyGroups) {
      for (const a of sortByOrder(g.attributes)) {
        if (isAssemblyAttribute(a)) continue;
        if (!isAttributeVisible(a, allSchemaAttributes, draft.attributes)) continue;
        if (a.type === "multiselect") {
          featureMulti.push(a);
          stableOrderFeature.set(a.id, posFeat++);
        } else if (a.type === "boolean") {
          featureBool.push(a);
          stableOrderFeature.set(a.id, posFeat++);
        } else {
          main.push(a);
          stableOrderMain.set(a.id, posMain++);
        }
      }
    }
    for (const a of assemblyAttrsList) {
      if (!isAttributeVisible(a, allSchemaAttributes, draft.attributes)) continue;
      main.push(a);
      stableOrderMain.set(a.id, posMain++);
    }
    featureMulti.sort((a, b) => (stableOrderFeature.get(a.id) ?? 0) - (stableOrderFeature.get(b.id) ?? 0));
    return {
      additionalMainAttrs: sortAdditionalMainAttrs(main, stableOrderMain),
      additionalFeatureBooleans: sortFeatureBooleanAttrs(featureBool, stableOrderFeature),
      additionalFeatureMultiselects: featureMulti,
    };
  }, [specOnlyGroups, assemblyAttrsList, allSchemaAttributes, draft.attributes]);

  const purchaseCostTypes = useMemo(
    () => listPurchaseCostTypeRecords(),
    [wizardStep, purchaseCostTypesVersion],
  );
  const purchaseCostCategories = useMemo(
    () => listPurchaseCostCategories(),
    [wizardStep, purchaseCostCategoriesVersion],
  );

  const documentTypes = useMemo(
    () => listDocumentTypeRecords(),
    [wizardStep, documentTypesVersion],
  );

  const additionalSubtotal = useMemo(
    () => sumPurchaseCostLines(draft.purchaseCostLines),
    [draft.purchaseCostLines],
  );

  const landedPreview = useMemo(
    () =>
      computeLandedCost(
        (draft.acquisitionType ?? "purchased") === "consignment" ? null : draft.purchasePrice,
        draft.purchaseCostLines,
      ),
    [draft.acquisitionType, draft.purchasePrice, draft.purchaseCostLines],
  );

  const expectedSalePrice =
    draft.salePrice != null && Number.isFinite(Number(draft.salePrice))
      ? Number(draft.salePrice)
      : draft.expectedSalePrice != null && Number.isFinite(Number(draft.expectedSalePrice))
        ? Number(draft.expectedSalePrice)
        : null;

  const effectiveDealType =
    (draft.acquisitionType ?? "purchased") === "consignment"
      ? "owner_vehicle"
      : (draft.dealType ?? "purchased");

  const showroomCommissionBase = useMemo(
    () =>
      showroomCommissionPreviewBase({
        commissionType: (draft.showroomCharge ?? draft.companyOwnerCommission)?.commissionType,
        dealType: effectiveDealType,
        profitRuleType: draft.profitRuleType,
        basePurchase: landedPreview,
        expectedSalePrice,
        netProfit:
          expectedSalePrice != null && landedPreview != null
            ? Number((expectedSalePrice - landedPreview).toFixed(2))
            : null,
      }),
    [
      draft.showroomCharge,
      draft.companyOwnerCommission,
      effectiveDealType,
      draft.profitRuleType,
      landedPreview,
      expectedSalePrice,
    ],
  );

  const partnerInvestmentPool = useMemo(
    () =>
      remainingInvestmentPool(
        landedPreview,
        null,
        effectiveDealType,
        draft.profitRuleType,
      ),
    [landedPreview, effectiveDealType, draft.profitRuleType],
  );

  const isOwnerSurplus = isOwnerVehicleSurplusDeal(effectiveDealType, draft.profitRuleType);

  const showroomForPaidBy = draft.showroomCharge ?? draft.companyOwnerCommission;
  const paidByPartyId = defaultPaidByPartyId(
    showroomForPaidBy?.partyId ?? companyParties[0]?.id ?? null,
  );
  const paidByOptions = useMemo(
    () =>
      buildPaidByOptions({
        dealType: effectiveDealType,
        companyParties,
        dealPartners: draft.dealPartners,
        parties,
      }),
    [effectiveDealType, companyParties, draft.dealPartners, parties],
  );

  const costBearerOptions = useMemo(
    () => [
      {
        value: "partnership",
        label: "Partnership (Split by Ratio)",
        kind: "special" as const,
        id: "partnership",
      },
      ...paidByOptions,
    ],
    [paidByOptions],
  );



  // Owner Vehicle + Owner Surplus: default Owner Demand Price from Base Purchase Cost.
  // Stops auto-sync once the user changes Owner Demand away from the tracked Base Purchase Cost.
  useEffect(() => {
    if (!isOwnerSurplus) {
      basePurchaseForOwnerDemandRef.current = null;
      return;
    }
    const basePurchase =
      draft.purchasePrice != null && Number.isFinite(Number(draft.purchasePrice))
        ? Number(draft.purchasePrice)
        : null;
    if (basePurchase == null) return;

    // Recognize values created by the previous Expected Sale Price auto-fill behavior.
    const legacyExpectedSale =
      draft.salePrice != null && Number.isFinite(Number(draft.salePrice))
        ? Number(draft.salePrice)
        : draft.expectedSalePrice != null && Number.isFinite(Number(draft.expectedSalePrice))
          ? Number(draft.expectedSalePrice)
          : null;

    const current =
      draft.ownerDemandPrice != null && Number.isFinite(Number(draft.ownerDemandPrice))
        ? Number(draft.ownerDemandPrice)
        : null;
    const lastAutoBase = basePurchaseForOwnerDemandRef.current;

    // Manual override: ODP differs from the current/last base and legacy auto-filled sale price,
    // or the field was cleared (current is null) after being auto-populated (lastAutoBase is not null).
    if (
      (current != null &&
        current !== basePurchase &&
        current !== legacyExpectedSale &&
        (lastAutoBase == null || current !== lastAutoBase)) ||
      (current == null && lastAutoBase != null)
    ) {
      return;
    }

    basePurchaseForOwnerDemandRef.current = basePurchase;
    if (current === basePurchase) return;
    setDraft((d) => ({ ...d, ownerDemandPrice: basePurchase }));
  }, [
    isOwnerSurplus,
    draft.purchasePrice,
    draft.salePrice,
    draft.expectedSalePrice,
    draft.ownerDemandPrice,
  ]);

  // Default Expected Sale Price from Total Landed Cost + 10% increment.
  // Stops auto-sync once the user changes Expected Sale Price away from the tracked target.
  const landedForExpectedSaleRef = useRef<number | null>(null);
  useEffect(() => {
    if (landedPreview == null) return;
    const targetSalePrice = Number((landedPreview * 1.1).toFixed(2));

    const current =
      draft.expectedSalePrice != null && Number.isFinite(Number(draft.expectedSalePrice))
        ? Number(draft.expectedSalePrice)
        : null;
    const lastAutoTarget = landedForExpectedSaleRef.current;

    if (
      (current != null && current !== targetSalePrice && (lastAutoTarget == null || current !== lastAutoTarget)) ||
      (current == null && lastAutoTarget != null)
    ) {
      return;
    }

    landedForExpectedSaleRef.current = targetSalePrice;
    if (current === targetSalePrice) return;
    setDraft((d) => ({ ...d, expectedSalePrice: targetSalePrice }));
  }, [landedPreview, draft.expectedSalePrice]);

  // Default Deal Partner Investment Amount from Total Landed Cost.
  // Stops auto-sync once the user changes Investment away from the tracked Landed Cost.
  // Purchased deals do not use Deal Partners — skip so we don't create a party-less invested row.
  const landedForPartnerInvestRef = useRef<number | null>(null);
  useEffect(() => {
    if (landedPreview == null) return;
    if (effectiveDealType === "purchased") return;

    const partners = draft.dealPartners ?? [];
    if (partners.length === 0) return;

    if (partners.length === 1) {
      const p = partners[0];
      const current = p.investmentAmount != null && Number.isFinite(Number(p.investmentAmount)) ? Number(p.investmentAmount) : null;
      const lastAutoTarget = landedForPartnerInvestRef.current;

      if (
        (current != null && current !== landedPreview && (lastAutoTarget == null || current !== lastAutoTarget)) ||
        (current == null && lastAutoTarget != null)
      ) {
        return;
      }

      landedForPartnerInvestRef.current = landedPreview;
      if (current === landedPreview) return;

      const updated = partners.map((row) => ({
        ...row,
        investmentAmount: landedPreview,
        investmentRatio: 100,
      }));
      setDraft((d) => ({ ...d, dealPartners: updated }));
    }
  }, [landedPreview, draft.dealPartners, effectiveDealType]);

  // Owner Vehicle + Owner Surplus:
  // - Showroom Charges = Profit % 100%
  // - Partner Investment = Base Purchase; partner profit override is cleared
  useEffect(() => {
    if (!isOwnerSurplus) return;

    const esp =
      draft.salePrice != null && Number.isFinite(Number(draft.salePrice))
        ? Number(draft.salePrice)
        : draft.expectedSalePrice != null && Number.isFinite(Number(draft.expectedSalePrice))
          ? Number(draft.expectedSalePrice)
          : null;

    const commissionOk =
      (draft.showroomCharge ?? draft.companyOwnerCommission)?.commissionType === "profit_percentage" &&
      Number((draft.showroomCharge ?? draft.companyOwnerCommission)?.commissionValue) === 100;
    const partnersOk = ownerVehicleSurplusPartnersMatch(
      draft.dealPartners,
      draft.purchasePrice,
      esp,
      draft.ownerDemandPrice,
    );
    if (commissionOk && partnersOk) return;

    setDraft((d) => {
      const expectedSale =
        d.salePrice != null && Number.isFinite(Number(d.salePrice))
          ? Number(d.salePrice)
          : d.expectedSalePrice != null && Number.isFinite(Number(d.expectedSalePrice))
            ? Number(d.expectedSalePrice)
            : null;

      const prev = d.showroomCharge ?? d.companyOwnerCommission ?? emptyShowroomCharge();
      const showroomCharge = emptyShowroomCharge({
        partyId: prev.partyId,
        partyName: prev.partyName,
        commissionType: "profit_percentage",
        commissionValue: 100,
      });

      return {
        ...d,
        showroomCharge,
        companyOwnerCommission: showroomCharge,
        dealPartners: applyOwnerVehicleSurplusPartnerDefaults({
          partners: d.dealPartners,
          basePurchase: d.purchasePrice,
          expectedSalePrice: expectedSale,
          ownerDemandPrice: d.ownerDemandPrice,
        }),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOwnerSurplus,
    draft.purchasePrice,
    draft.salePrice,
    draft.expectedSalePrice,
    draft.ownerDemandPrice,
    draft.dealPartners?.length,
    (draft.dealPartners ?? []).map((p) => p.key).join("|"),
  ]);

  // Cap partner investments to Base Purchase Cost when the base shrinks; keep ratios in sync.
  // Manual Investment Amount edits are preserved (no equal auto-split).
  useEffect(() => {
    if (isOwnerSurplus) return;
    const partners = draft.dealPartners ?? [];
    if (partners.length === 0) return;
    const pool = partnerInvestmentPool;
    if (pool == null || !Number.isFinite(pool)) {
      const synced = withRecalculatedInvestmentRatios(partners, pool);
      const drifted = synced.some((row, i) => {
        const cur = partners[i];
        if (!cur) return true;
        const a = cur.investmentRatio;
        const b = row.investmentRatio;
        if (a == null && b == null) return false;
        if (a == null || b == null) return true;
        return Math.abs(Number(a) - Number(b)) > 0.0005;
      });
      if (drifted) setDraft((d) => ({ ...d, dealPartners: synced }));
      return;
    }

    const total = investmentAmountTotal(partners);
    if (total <= pool + 0.009) {
      const synced = withRecalculatedInvestmentRatios(partners, pool);
      const drifted = synced.some((row, i) => {
        const cur = partners[i];
        if (!cur) return true;
        const a = cur.investmentRatio;
        const b = row.investmentRatio;
        if (a == null && b == null) return false;
        if (a == null || b == null) return true;
        return Math.abs(Number(a) - Number(b)) > 0.0005;
      });
      if (drifted) setDraft((d) => ({ ...d, dealPartners: synced }));
      return;
    }

    // Shrink from the last partners until the total fits the new Base Purchase Cost.
    let next = [...partners];
    let excess = Number((total - pool).toFixed(2));
    for (let i = next.length - 1; i >= 0 && excess > 0.009; i--) {
      const current = Number(next[i]?.investmentAmount) || 0;
      if (current <= 0) continue;
      const reduce = Math.min(current, excess);
      next = applyPartnerInvestmentAmountEdit(next, i, Number((current - reduce).toFixed(2)), pool);
      excess = Number((excess - reduce).toFixed(2));
    }
    setDraft((d) => ({ ...d, dealPartners: withRecalculatedInvestmentRatios(next, pool) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOwnerSurplus,
    partnerInvestmentPool,
    draft.dealPartners?.length,
    (draft.dealPartners ?? []).map((p) => `${p.key}:${p.investmentAmount ?? ""}`).join("|"),
  ]);

  // Showroom Charges (Fixed/%) and Deal Partner Profit % are independent —
  // neither side auto-rewrites the other.

  // Partnership: default Profit % to an equal split of 100% among selected partners.
  // Respects existing values when every selected partner already has a Profit %.
  useEffect(() => {
    if (!isShowroomIndependentOfPartnerProfits(effectiveDealType, draft.profitRuleType)) return;
    const partners = draft.dealPartners ?? [];
    const selected = partners.filter((p) => p.partyId);
    if (selected.length === 0) return;
    if (partnerProfitsMatchRemaining(partners, 100)) return;
    const allHaveProfit = selected.every(
      (p) =>
        (p.profitType === "percentage" || p.profitType == null) &&
        p.profitValue != null &&
        Number.isFinite(Number(p.profitValue)),
    );
    if (allHaveProfit) return;
    setDraft((d) => ({
      ...d,
      dealPartners: applyRemainingProfitPercentToPartners(d.dealPartners, 100),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveDealType,
    draft.profitRuleType,
    (draft.dealPartners ?? []).map((p) => `${p.key}:${p.partyId ?? ""}`).join("|"),
  ]);

  const categoryLabel = schema?.category.name?.trim() || "Product";
  const productName = draft.name?.trim() || "";
  const title = isEdit
    ? productName
      ? `Edit ${categoryLabel} — ${productName}`
      : `Edit ${categoryLabel}`
    : schema
      ? `Add New ${categoryLabel}`
      : "Add Product";

  const productImages = draft.productImages ?? [];
  const productDocuments = draft.productDocuments ?? [];
  const isEditLocked =
    isEdit && (draft.inventoryStatus === "sold" || draft.inventoryStatus === "reserved");

  const persistSnapshot = (step: number, d: ProductDraft, productId: string | null) => {
    if (isEdit) return;
    saveAddProductWizard({
      step,
      draft: stripLocalFilesFromDraft(savePayload(d)),
      ...(productId ? { persistedProductId: productId } : {}),
    });
  };

  const handleCancel = () => {
    if (isEdit) {
      navigate(routeId ? `/products/${routeId}` : "/products");
      return;
    }
    const id = persistedIdRef.current;
    if (id) {
      const p = getProductById(id);
      if (p && (p.inventoryStatus ?? "draft") === "draft") {
        void deleteProduct(id).catch(() => {
          /* best-effort cleanup */
        });
      }
    }
    clearAddProductWizard();
    navigate("/products");
  };

  const goNext = async () => {
    if (saveLoading || isEditLocked) return;
    const d = draftRef.current;
    if (currentStepId === "basic") {
      if (!validateBasicStep(d)) return;
      if (
        !validateVisibleRequiredAttributes(d, basicSpecGroups, (msg) => toast.error(msg), (a) =>
          isAssemblyAttribute(a),
        )
      ) {
        return;
      }
    }
    if (currentStepId === "spec") {
      if (!validateAdditionalInformationStep(d, specOnlyGroups, assemblyAttrsList, allSchemaAttributes)) return;
    }
    if (currentStepId === "media") {
      if (!validateDocumentMeta(d)) return;
    }
    if (currentStepId === "purchase") {
      const dealTypeForValidate =
        (d.acquisitionType ?? "purchased") === "consignment"
          ? "owner_vehicle"
          : (d.dealType ?? "purchased");
      const dealError = validateDealDraft({
        dealType: dealTypeForValidate,
        profitRuleType: d.profitRuleType,
        ownerDemandPrice: d.ownerDemandPrice,
        dealPartners: d.dealPartners ?? [],
        showroomCharge: d.showroomCharge ?? d.companyOwnerCommission,
        basePurchaseCost: computeLandedCost(
          (d.acquisitionType ?? "purchased") === "consignment" ? null : d.purchasePrice,
          d.purchaseCostLines,
        ),
      });
      if (dealError) {
        toast.error(dealError);
        return;
      }
    }
    const next = Math.min(wizardStep + 1, stepCount - 1);
    if (next === wizardStep) return;
    setSaveLoading(true);
    try {
      const normalized = savePayload(d);
      if (isEdit) {
        if (!routeId) throw new Error("Missing product id");
        await updateProduct(routeId, normalized);
        setWizardStep(next);
        toast.success("Saved.");
      } else {
        const { id } = await persistProductWizardProgress(normalized, persistedIdRef.current);
        setPersistedProductId(id);
        persistedIdRef.current = id;
        persistSnapshot(next, d, id);
        setWizardStep(next);
        toast.success("Saved to catalog.");
      }
    } catch (e) {
      toast.error(catalogProductErrorMessage(e));
    } finally {
      setSaveLoading(false);
    }
  };

  const goBack = () => {
    const d = draftRef.current;
    const prev = Math.max(0, wizardStep - 1);
    if (prev === wizardStep) return;
    persistSnapshot(prev, d, persistedIdRef.current);
    setWizardStep(prev);
  };

  const finishWizard = async (publish = false) => {
    if (saveLoading || isEditLocked) return;
    const d = draftRef.current;
    if (!validateBasicStep(d)) {
      setWizardStep(0);
      return;
    }
    if (
      !validateVisibleRequiredAttributes(d, basicSpecGroups, (msg) => toast.error(msg), (a) =>
        isAssemblyAttribute(a),
      )
    ) {
      setWizardStep(0);
      return;
    }
    if (!validateAdditionalInformationStep(d, specOnlyGroups, assemblyAttrsList, allSchemaAttributes)) {
      const specIdx = wizardSteps.findIndex((s) => s.id === "spec");
      if (specIdx >= 0) setWizardStep(specIdx);
      return;
    }
    if (!validateDocumentMeta(d)) {
      const mediaIdx = wizardSteps.findIndex((s) => s.id === "media");
      if (mediaIdx >= 0) setWizardStep(mediaIdx);
      return;
    }
    if (canViewPrices) {
      const dealTypeForValidate =
        (d.acquisitionType ?? "purchased") === "consignment"
          ? "owner_vehicle"
          : (d.dealType ?? "purchased");
      const dealError = validateDealDraft({
        dealType: dealTypeForValidate,
        profitRuleType: d.profitRuleType,
        ownerDemandPrice: d.ownerDemandPrice,
        dealPartners: d.dealPartners ?? [],
        showroomCharge: d.showroomCharge ?? d.companyOwnerCommission,
        basePurchaseCost: computeLandedCost(
          (d.acquisitionType ?? "purchased") === "consignment" ? null : d.purchasePrice,
          d.purchaseCostLines,
        ),
      });
      if (dealError) {
        const purchaseIdx = wizardSteps.findIndex((s) => s.id === "purchase");
        if (purchaseIdx >= 0) setWizardStep(purchaseIdx);
        toast.error(dealError);
        return;
      }
    }
    let id = persistedIdRef.current;
    const shouldPublish = publish && (d.inventoryStatus ?? "draft") === "draft";
    const payload = savePayload(
      shouldPublish ? { ...d, inventoryStatus: "in_stock" } : d,
    );
    setSaveLoading(true);
    try {
      if (isEdit) {
        if (!routeId) throw new Error("Missing product id");
        await updateProduct(routeId, payload);
        toast.success(shouldPublish ? "Product saved and published." : "Product saved.");
        navigate(`/products/${routeId}`);
        return;
      }
      if (!id) {
        const created = await persistProductWizardProgress(payload, null);
        id = created.id;
        setPersistedProductId(id);
        persistedIdRef.current = id;
        persistSnapshot(wizardStep, d, id);
      }
      await finalizeWizardProduct(payload, id);
      clearAddProductWizard();
      toast.success(shouldPublish ? "Product saved and published." : "Product saved.");
      navigate("/products");
    } catch (e) {
      toast.error(catalogProductErrorMessage(e));
    } finally {
      setSaveLoading(false);
    }
  };

  const isLastStep = wizardStep >= stepCount - 1;

  const ingestDocFiles = async (fileList: File[]) => {
    const good = fileList.filter((f) => isAllowedProductDocFile(f));
    if (good.length === 0) return;

    const current = draftRef.current;
    if (!current.documentCategory?.trim()) {
      toast.error("Select a document type before adding files.");
      return;
    }
    const copy = current.documentType === "duplicate" ? "duplicate" : "original";
    const pagesRaw = Number(current.numberOfDocuments ?? 0);
    const pages = Number.isInteger(pagesRaw) && pagesRaw >= 0 ? Math.min(100, pagesRaw) : 0;

    const max = PRODUCT_DOCS_ATTR.maxFiles ?? 20;
    const base = current.productDocuments ?? [];
    const uploads = [...base];
    for (const f of good) {
      if (uploads.length >= max) break;
      const uploaded = await mockUploadFile(f, PRODUCT_DOCS_ATTR);
      uploads.push({
        ...uploaded,
        documentTypeId: current.documentCategoryId ?? null,
        documentTypeName: current.documentCategory?.trim() || null,
        documentCopy: copy,
        numberOfPages: pages,
      });
    }
    setDraft((d) => ({
      ...d,
      productDocuments: uploads,
      documentCategory: null,
      documentCategoryId: null,
      documentType: "original",
      numberOfDocuments: undefined,
    }));
  };

  const buildInitialCostLineModalForm = (typeIdOverride?: string): PurchaseCostLine => {
    const lines = draftRef.current.purchaseCostLines ?? [];
    const used = new Set(lines.map((l) => l.typeId));
    const availableType = purchaseCostTypes.find((t) => !used.has(t.id));
    const typeId = typeIdOverride ?? availableType?.id ?? purchaseCostTypes[0]?.id ?? "";

    const showroom = draftRef.current.showroomCharge ?? draftRef.current.companyOwnerCommission;
    const partyId = defaultPaidByPartyId(
      showroom?.partyId ?? companyParties[0]?.id ?? null,
    );
    const currCode = draftRef.current.purchaseCurrency ?? getActiveCompanyCurrency().code;
    const exRate = draftRef.current.purchaseExchangeRate ?? 1;

    return {
      typeId,
      amount: 0,
      currencyCode: currCode,
      currencyAmount: 0,
      exchangeRate: exRate,
      paidByPartyId: null,
      paidToPartyId: null,
      costBearerPartyId: "partnership",
      paymentMethod: "cash",
      bankName: null,
      transactionReferenceNo: null,
      payOrderNo: null,
      description: null,
    };
  };

  const openAddCostLineModal = () => {
    const lines = draft.purchaseCostLines ?? [];
    const used = new Set(lines.map((l) => l.typeId));
    const available = purchaseCostTypes.find((t) => !used.has(t.id));
    if (!available && purchaseCostTypes.length > 0) {
      toast.error("Every cost type is already on this product.");
      return;
    }
    setEditingCostLineIndex(null);
    setCostLineModalForm(buildInitialCostLineModalForm());
    setCostLineModalOpen(true);
  };

  const openEditCostLineModal = (index: number) => {
    const line = (draft.purchaseCostLines ?? [])[index];
    if (!line) return;
    setEditingCostLineIndex(index);
    setCostLineModalForm({ ...line });
    setCostLineModalOpen(true);
  };

  const handleSaveCostLineModal = (andAddMore = false) => {
    if (!costLineModalForm.typeId) {
      toast.error("Please select a cost type.");
      return;
    }

    const activeBase = getActiveCompanyCurrency().code;
    const cCode = costLineModalForm.currencyCode ?? draft.purchaseCurrency ?? activeBase;
    const isBase = cCode === activeBase;
    const exRate = isBase ? 1 : costLineModalForm.exchangeRate == null || !Number.isFinite(Number(costLineModalForm.exchangeRate)) ? 1 : Number(costLineModalForm.exchangeRate);
    const currAmt = costLineModalForm.currencyAmount == null || !Number.isFinite(Number(costLineModalForm.currencyAmount)) ? 0 : Number(costLineModalForm.currencyAmount);
    const baseAmt = isBase ? currAmt : Number((currAmt * exRate).toFixed(2));

    const updatedLine: PurchaseCostLine = {
      ...costLineModalForm,
      currencyCode: cCode,
      exchangeRate: exRate,
      currencyAmount: currAmt,
      amount: baseAmt,
    };

    setDraft((d) => {
      const list = [...(d.purchaseCostLines ?? [])];
      if (editingCostLineIndex != null && editingCostLineIndex >= 0 && editingCostLineIndex < list.length) {
        list[editingCostLineIndex] = updatedLine;
      } else {
        list.push(updatedLine);
      }
      return { ...d, purchaseCostLines: list };
    });

    if (andAddMore && editingCostLineIndex == null) {
      toast.success("Cost line added.");
      const remainingUsed = new Set([...(draft.purchaseCostLines ?? []).map((l) => l.typeId), updatedLine.typeId]);
      const nextType = purchaseCostTypes.find((t) => !remainingUsed.has(t.id));
      setCostLineModalForm(buildInitialCostLineModalForm(nextType?.id));
    } else {
      toast.success(editingCostLineIndex != null ? "Cost line updated." : "Cost line added.");
      setCostLineModalOpen(false);
    }
  };

  const addCostLine = () => {
    openAddCostLineModal();
  };

  const openCostTypeDialog = (lineIndex: number | null) => {
    setCostTypeDialogLineIndex(lineIndex);
    setCostTypeLabel("");
    const cats = listPurchaseCostCategories();
    setCostTypeCategoryId(cats[0]?.id ?? "");
    setCostTypeDialogOpen(true);
    void syncPurchaseCostCategoriesFromApi().then(() => {
      setPurchaseCostCategoriesVersion((v) => v + 1);
    });
  };

  const handleCreateCostType = async () => {
    const label = costTypeLabel.trim();
    if (!costTypeCategoryId) {
      toast.error("Purchase cost category is required.");
      return;
    }
    if (!label) {
      toast.error("Cost type name is required.");
      return;
    }
    setCostTypeSaving(true);
    try {
      const { record, message } = await createPurchaseCostType({
        label,
        purchaseCostCategoryId: costTypeCategoryId,
      });
      await syncPurchaseCostTypesFromApi().catch(() => undefined);
      setPurchaseCostTypesVersion((v) => v + 1);

      const lineIndex = costTypeDialogLineIndex;
      setDraft((d) => {
        const lines = [...(d.purchaseCostLines ?? [])];
        if (lineIndex != null && lineIndex >= 0 && lineIndex < lines.length) {
          lines[lineIndex] = { ...lines[lineIndex], typeId: record.id };
          return { ...d, purchaseCostLines: lines };
        }
        const showroom = d.showroomCharge ?? d.companyOwnerCommission;
        const partyId = defaultPaidByPartyId(
          showroom?.partyId ?? companyParties[0]?.id ?? null,
        );
        const currCode = d.purchaseCurrency ?? getActiveCompanyCurrency().code;
        const exRate = d.purchaseExchangeRate ?? 1;
        return {
          ...d,
          purchaseCostLines: [
            ...lines,
            {
              typeId: record.id,
              amount: 0,
              currencyCode: currCode,
              currencyAmount: 0,
              exchangeRate: exRate,
              paidByPartyId: partyId,
              paymentMethod: "cash" as const,
              bankName: null,
              transactionReferenceNo: null,
              payOrderNo: null,
              description: null,
            },
          ],
        };
      });

      toast.success(message || "Cost type created.");
      setCostTypeDialogOpen(false);
      setCostTypeDialogLineIndex(null);
    } catch (e) {
      toast.error(catalogProductErrorMessage(e));
    } finally {
      setCostTypeSaving(false);
    }
  };

  const handleCreateDocumentType = async () => {
    const name = documentTypeName.trim();
    if (!name) {
      toast.error("Document type name is required.");
      return;
    }
    const exists = listDocumentTypeRecords().some(
      (t) => t.name.toLowerCase() === name.toLowerCase(),
    );
    if (exists) {
      toast.error("A document type with this name already exists.");
      return;
    }
    setDocumentTypeSaving(true);
    try {
      const { record, message } = await createDocumentType({ name });
      await syncDocumentTypesFromApi().catch(() => undefined);
      setDocumentTypesVersion((v) => v + 1);
      setDraft((d) => ({
        ...d,
        documentCategory: record.name,
        documentCategoryId: record.id,
      }));
      toast.success(message || "Document type created.");
      setDocumentTypeDialogOpen(false);
      setDocumentTypeName("");
    } catch (e) {
      toast.error(catalogProductErrorMessage(e));
    } finally {
      setDocumentTypeSaving(false);
    }
  };

  const updateCostLine = (
    index: number,
    patch: Partial<{
      typeId: string;
      amount: number;
      currencyCode: string | null;
      currencyAmount: number | null;
      exchangeRate: number | null;
      paidByPartyId: string | null;
      paymentMethod: "cash" | "online" | "pay_order" | null;
      bankName: string | null;
      transactionReferenceNo: string | null;
      payOrderNo: string | null;
      description: string | null;
    }>,
  ) => {
    setDraft((d) => {
      const list = [...(d.purchaseCostLines ?? [])];
      const prevRow = list[index];
      if (!prevRow) return d;
      const row = { ...prevRow, ...patch };
      if (
        typeof patch.currencyAmount !== "undefined" ||
        typeof patch.exchangeRate !== "undefined" ||
        typeof patch.currencyCode !== "undefined"
      ) {
        const activeBase = getActiveCompanyCurrency().code;
        const cCode =
          typeof patch.currencyCode !== "undefined"
            ? patch.currencyCode
            : row.currencyCode ?? d.purchaseCurrency ?? activeBase;
        const isBase = cCode === activeBase;
        const exRate = isBase
          ? 1
          : typeof patch.exchangeRate !== "undefined"
          ? patch.exchangeRate ?? 1
          : row.exchangeRate ?? d.purchaseExchangeRate ?? 1;
        const cAmt =
          typeof patch.currencyAmount !== "undefined"
            ? patch.currencyAmount
            : row.currencyAmount ?? row.amount;
        row.currencyCode = cCode;
        row.exchangeRate = exRate;
        row.currencyAmount = cAmt;
        row.amount = cAmt != null && exRate != null ? cAmt * exRate : 0;
      } else if (typeof patch.amount !== "undefined") {
        const n = Number(patch.amount);
        row.amount = Number.isFinite(n) && n >= 0 ? n : 0;
      }
      if (typeof patch.description !== "undefined") {
        const raw = patch.description;
        row.description = raw == null || raw === "" ? null : raw;
      }
      list[index] = row;
      return { ...d, purchaseCostLines: list };
    });
  };

  const updateCostLinePaymentMethod = (index: number, method: PaymentMethodValue) => {
    setDraft((d) => {
      const list = [...(d.purchaseCostLines ?? [])];
      const prevRow = list[index];
      if (!prevRow) return d;
      const next = applyPaymentMethodChange(
        emptyPaymentMethodDraft({
          paymentMethod: (prevRow.paymentMethod ?? "") as PaymentMethodValue,
          bankName: prevRow.bankName ?? "",
          transactionReferenceNo: prevRow.transactionReferenceNo ?? "",
          payOrderNo: prevRow.payOrderNo ?? "",
        }),
        method,
      );
      list[index] = {
        ...prevRow,
        paymentMethod: next.paymentMethod || null,
        bankName: next.bankName.trim() || null,
        transactionReferenceNo: next.transactionReferenceNo.trim() || null,
        payOrderNo: next.payOrderNo.trim() || null,
      };
      return { ...d, purchaseCostLines: list };
    });
  };

  const removeCostLine = (index: number) => {
    setDraft((d) => ({
      ...d,
      purchaseCostLines: (d.purchaseCostLines ?? []).filter((_, i) => i !== index),
    }));
  };

  const canAddCostLine =
    purchaseCostTypes.length > 0 &&
    purchaseCostTypes.some((t) => !(draft.purchaseCostLines ?? []).some((l) => l.typeId === t.id));

  if (isEdit && !editReady) {
    return (
      <Layout title="Edit product">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading product…
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={title}>
      <div className="w-full max-w-none space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
              onClick={handleCancel}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-foreground">{title}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Step {wizardStep + 1} of {stepCount}.{" "}
                <span className="text-foreground/80">Save &amp; Next</span> writes to the catalog store (same session is kept in your browser).
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
            <Button type="button" variant="glass" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>

        <div className="w-full space-y-2">
          <div className="overflow-x-auto rounded-xl border border-border/50 bg-card/40 px-3 py-5 sm:px-6">
            <div className="flex min-w-[720px] items-center sm:min-w-0">
              {wizardSteps.map((s, i) => {
                const done = i < wizardStep;
                const active = i === wizardStep;
                const lineComplete = i < wizardStep;
                return (
                  <Fragment key={s.id}>
                    <button
                      type="button"
                      onClick={() => setWizardStep(i)}
                      className={cn(
                        "flex shrink-0 items-center gap-2 sm:gap-3 rounded-lg px-1.5 py-1 -mx-0.5 text-left outline-none transition-colors",
                        "hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      )}
                      aria-label={`Go to ${s.label}`}
                      aria-current={active ? "step" : undefined}
                    >
                      <div
                        className={cn(
                          "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors pointer-events-none",
                          done && "bg-[#1a56db] text-white shadow-sm",
                          active && !done && "bg-[#f05252] text-white shadow-sm",
                          !done && !active && "bg-[#f3f4f6] text-[#4b5563]",
                        )}
                      >
                        {done ? (
                          <Check className="h-5 w-5 stroke-[3]" aria-hidden />
                        ) : active ? (
                          <X className="h-5 w-5 stroke-[3]" aria-hidden />
                        ) : (
                          <span>{i + 1}</span>
                        )}
                      </div>
                      <span
                        className={cn(
                          "max-w-[5.5rem] truncate text-xs font-medium sm:max-w-[9rem] sm:text-sm pointer-events-none",
                          active ? "text-foreground" : done ? "text-foreground/90" : "text-muted-foreground",
                        )}
                        title={s.label}
                      >
                        {s.label}
                      </span>
                    </button>
                    {i < wizardSteps.length - 1 ? (
                      <div
                        className={cn(
                          "mx-2 h-0.5 min-w-[1.25rem] flex-1 rounded-full sm:mx-3",
                          lineComplete ? "bg-[#1a56db]" : "bg-[#e5e7eb]",
                        )}
                        aria-hidden
                      />
                    ) : null}
                  </Fragment>
                );
              })}
            </div>
          </div>

          {currentStepId === "basic" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
              <SectionHeader title="Basic information" subtitle="Category, listing fields, and core vehicle details" />
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Category *</Label>
                  <Select
                    value={draft.categoryId}
                    onValueChange={(v) => {
                      setDraft((d) => ({ ...d, categoryId: v, attributes: {} }));
                    }}
                  >
                    <SelectTrigger className="glass-input h-10">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Name *</Label>
                  <Input
                    className="glass-input h-10 cursor-pointer"
                    readOnly
                    value={draft.name}
                    onClick={() => setVehiclePickerOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setVehiclePickerOpen(true);
                      }
                    }}
                    placeholder="Click to select model year, make, model…"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Slug *</Label>
                  <Input
                    className="glass-input h-10"
                    value={draft.slug}
                    onChange={(e) => setDraft((d) => ({ ...d, slug: slugify(e.target.value) }))}
                    placeholder="Auto-generated from name"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Location</Label>
                  <Select
                    value={draft.warehouseId && draft.warehouseId !== "" ? draft.warehouseId : "__none__"}
                    onValueChange={(v) => setDraft((d) => ({ ...d, warehouseId: v === "__none__" ? null : v }))}
                  >
                    <SelectTrigger className="glass-input h-10">
                      <SelectValue placeholder="Optional" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No location</SelectItem>
                      {warehouses.map((w) => (
                        <SelectItem key={w.id} value={w.id}>
                          {w.name}
                          {w.city ? ` — ${w.city}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {basicSpecGroups.some((group) =>
                  filterVisibleAttributes(
                    filterBasicSpecGridAttributes(group.attributes),
                    allSchemaAttributes,
                    draft.attributes,
                  ).length > 0,
                ) ? (
                  <div className="space-y-4 md:col-span-2 xl:col-span-3">
                    {basicSpecGroups.map((group) => {
                      const visibleAttrs = filterVisibleAttributes(
                        filterBasicSpecGridAttributes(group.attributes),
                        allSchemaAttributes,
                        draft.attributes,
                      );
                      if (visibleAttrs.length === 0) return null;
                      return (
                        <div
                          key={group.id}
                          className={
                            isBasicDetailsAttributeGroup(group)
                              ? undefined
                              : "rounded-lg border border-border/40 bg-muted/10 p-4"
                          }
                        >
                          {!isBasicDetailsAttributeGroup(group) && group.name ? (
                            <p className="mb-3 text-sm font-semibold text-foreground">{group.name}</p>
                          ) : null}
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                            {visibleAttrs.map((attr) => (
                              <AttributeField
                                key={attr.id}
                                attribute={attr}
                                value={draft.attributes[attr.code]}
                                onChange={(v) => updateProductAttribute(attr.code, v, attr)}
                                onAttributeOptionsUpdated={handleAttributeOptionsUpdated}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                <div className="space-y-2 md:col-span-2 xl:col-span-3">
                  <Label className="text-xs text-muted-foreground">Description</Label>
                  <Textarea
                    className="glass-input min-h-[120px]"
                    value={draft.description ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                    placeholder="Public description shown on listings…"
                  />
                </div>
              </div>
            </motion.div>
          ) : currentStepId === "spec" ? (
            <>
              {!draft.categoryId ? (
                <div className="glass-card p-8 text-sm text-muted-foreground text-center">
                  Select a <strong className="text-foreground">category</strong> under Basic information to load additional information fields.
                </div>
              ) : loadingSchema ? (
                <div className="glass-card p-8 text-sm text-muted-foreground text-center">Loading category fields…</div>
              ) : schema && specOnlyGroups.length === 0 ? (
                <div className="glass-card p-8 text-sm text-muted-foreground text-center space-y-2">
                  <p>
                    This category has <strong className="text-foreground">no linked attributes for additional information</strong> yet.
                  </p>
                  <p>
                    In <strong className="text-foreground">Categories</strong>, open this category and attach attributes to the category schema so they appear here.
                  </p>
                </div>
              ) : schema ? (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card overflow-hidden p-6 md:p-8"
                >
                  <div className="mb-6 flex flex-col gap-3 border-b border-border/50 pb-5 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1">
                      <h2 className="font-display text-lg font-semibold tracking-tight text-foreground">Additional Information</h2>
                      <p className="max-w-2xl text-xs leading-relaxed text-muted-foreground">
                        All fields for this step are shown below. Values are saved with the product when you use{" "}
                        <span className="font-medium text-foreground/90">Save &amp; Next</span> or finish the wizard.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-8">
                    {additionalMainAttrs.length > 0 ? (
                      <div className="rounded-lg border border-border/40 bg-muted/10 p-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                          {additionalMainAttrs.map((attr) => (
                            <AttributeField
                              key={attr.id}
                              attribute={attr}
                              value={draft.attributes[attr.code]}
                              onChange={(v) => updateProductAttribute(attr.code, v, attr)}
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {additionalFeatureBooleans.length > 0 || additionalFeatureMultiselects.length > 0 ? (
                      <div className="space-y-3">
                        <h3 className="text-base font-semibold text-foreground">
                          Features
                          {additionalFeatureBooleans.some((a) => a.isRequired) ||
                          additionalFeatureMultiselects.some((a) => a.isRequired) ? (
                            <span className="text-destructive"> *</span>
                          ) : null}
                        </h3>
                        <div className="rounded-lg border border-border/40 bg-card/30 p-3 sm:p-4">
                          {additionalFeatureBooleans.length > 0 ? (
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 md:gap-2 md:gap-y-3">
                              {additionalFeatureBooleans.map((attr) => (
                                <AttributeField
                                  key={attr.id}
                                  layout="additional"
                                  featureGridCell
                                  attribute={attr}
                                  value={draft.attributes[attr.code]}
                                  onChange={(v) => updateProductAttribute(attr.code, v, attr)}
                                onAttributeOptionsUpdated={handleAttributeOptionsUpdated}
                                />
                              ))}
                            </div>
                          ) : null}
                          {additionalFeatureMultiselects.length > 0 ? (
                            <div
                              className={cn(
                                "space-y-4",
                                additionalFeatureBooleans.length > 0 ? "mt-6 border-t border-border/45 pt-6" : "",
                              )}
                            >
                              {additionalFeatureMultiselects.map((attr) => (
                                <div key={attr.id}>
                                  {additionalFeatureMultiselects.length > 1 ? (
                                    <p className="mb-3 text-sm font-medium text-foreground">
                                      {attr.name}
                                      {attr.isRequired ? <span className="text-destructive"> *</span> : null}
                                    </p>
                                  ) : null}
                                  <AttributeField
                                    layout="additional"
                                    suppressMultiselectLabel={additionalFeatureMultiselects.length <= 1}
                                    attribute={attr}
                                    value={draft.attributes[attr.code]}
                                    onChange={(v) => updateProductAttribute(attr.code, v, attr)}
                                onAttributeOptionsUpdated={handleAttributeOptionsUpdated}
                                  />
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </motion.div>
              ) : (
                <div className="glass-card p-8 text-sm text-muted-foreground text-center">
                  Could not load additional information for this category.
                </div>
              )}
            </>
          ) : currentStepId === "media" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 md:p-8">
              <SectionHeader
                title="Images & documents"
                subtitle="Gallery photos and supporting files — use the tabs below."
                icon={Camera}
              />

              <Tabs defaultValue="images" className="w-full">
                <TabsList className="grid h-auto w-full max-w-lg grid-cols-2 gap-1 p-1">
                  <TabsTrigger value="images" className="gap-2 px-3">
                    <Camera className="h-4 w-4 shrink-0" aria-hidden />
                    <span>Images</span>
                    {productImages.length > 0 ? (
                      <span className="ml-0.5 rounded-full bg-background/80 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground shadow-sm">
                        {productImages.length}
                      </span>
                    ) : null}
                  </TabsTrigger>
                  <TabsTrigger value="documents" className="gap-2 px-3">
                    <FileText className="h-4 w-4 shrink-0" aria-hidden />
                    <span>Documents</span>
                    {productDocuments.length > 0 ? (
                      <span className="ml-0.5 rounded-full bg-background/80 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground shadow-sm">
                        {productDocuments.length}
                      </span>
                    ) : null}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="images" className="mt-4 focus-visible:outline-none">
                  <ProductImageGallery
                    images={productImages}
                    productId={isEdit ? routeId ?? null : persistedProductId}
                    disabled={saveLoading}
                    dropzoneClassName="py-12"
                    onChange={(nextImages) => setDraft((d) => ({ ...d, productImages: nextImages }))}
                  />
                </TabsContent>

                <TabsContent value="documents" className="mt-4 space-y-3 focus-visible:outline-none">
                  <input
                    ref={docInputRef}
                    type="file"
                    multiple
                    accept={PRODUCT_DOCS_ATTR.accept}
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      e.currentTarget.value = "";
                      void ingestDocFiles(files);
                    }}
                  />
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label className="flex items-center justify-between w-full text-xs text-muted-foreground">
                        <span>Type</span>
                        {canCreateDocumentType ? (
                          <button
                            type="button"
                            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10"
                            aria-label="Add document type"
                            title="Add document type"
                            disabled={saveLoading}
                            onClick={() => {
                              setDocumentTypeName("");
                              setDocumentTypeDialogOpen(true);
                            }}
                          >
                            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                          </button>
                        ) : null}
                      </Label>
                      <DocumentTypeSelect
                        value={draft.documentCategory}
                        options={documentTypes}
                        onChange={(v) => {
                          const match = documentTypes.find((t) => t.name === v);
                          const id = match?.id ? String(match.id) : null;
                          setDraft((d) => ({
                            ...d,
                            documentCategory: v,
                            documentCategoryId: id && !id.startsWith("seed-") ? id : null,
                          }));
                        }}
                        disabled={saveLoading}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Document Copy</Label>
                      <Select
                        value={draft.documentType === "duplicate" ? "duplicate" : "original"}
                        onValueChange={(v) =>
                          setDraft((d) => ({
                            ...d,
                            documentType: v === "duplicate" ? "duplicate" : "original",
                          }))
                        }
                        disabled={saveLoading}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="Document copy" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="original">Original</SelectItem>
                          <SelectItem value="duplicate">Duplicate</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground" htmlFor="number-of-pages">
                        No. of Pages
                      </Label>
                      <Input
                        id="number-of-pages"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={100}
                        step={1}
                        className="glass-input h-10"
                        value={draft.numberOfDocuments ?? ""}
                        disabled={saveLoading}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === "") {
                            setDraft((d) => ({ ...d, numberOfDocuments: undefined }));
                            return;
                          }
                          if (!/^\d+$/.test(raw)) return;
                          const n = Number(raw);
                          if (!Number.isInteger(n)) return;
                          if (n < 0 || n > 100) return;
                          setDraft((d) => ({ ...d, numberOfDocuments: n }));
                        }}
                        onBlur={() => {
                          const n = Number(draftRef.current.numberOfDocuments);
                          if (!Number.isInteger(n) || n < 0) {
                            setDraft((d) => ({ ...d, numberOfDocuments: 0 }));
                          } else if (n > 100) {
                            setDraft((d) => ({ ...d, numberOfDocuments: 100 }));
                          }
                        }}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    className={cn(
                      "w-full rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      docDropActive
                        ? "border-primary bg-primary/10"
                        : "border-border bg-muted/20 hover:bg-muted/35 hover:border-primary/45",
                    )}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDocDropActive(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDocDropActive(true);
                    }}
                    onDragLeave={(e) => {
                      const next = e.relatedTarget as Node | null;
                      if (!next || !e.currentTarget.contains(next)) setDocDropActive(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDocDropActive(false);
                      void ingestDocFiles(Array.from(e.dataTransfer.files));
                    }}
                    onClick={() => docInputRef.current?.click()}
                  >
                    <Upload className="w-10 h-10 mx-auto text-muted-foreground/80 mb-3" aria-hidden />
                    <p className="text-sm font-medium text-foreground">
                      Drag &amp; drop documents here or click to browse (multiple)
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      PDF, Word, Excel, images — up to 10MB each · max {PRODUCT_DOCS_ATTR.maxFiles} files
                    </p>
                  </button>
                  {productDocuments.length > 0 ? (
                    <div className="space-y-3 pt-2 border-t border-border/40 overflow-x-auto">
                      <table className="w-full min-w-[640px] text-sm">
                        <thead>
                          <tr className="border-b border-border/50 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                            <th className="py-2 pr-2 font-medium w-12">Sr. #</th>
                            <th className="py-2 pr-2 font-medium">File</th>
                            <th className="py-2 pr-2 font-medium">Type</th>
                            <th className="py-2 pr-2 font-medium">Document Copy</th>
                            <th className="py-2 pr-2 font-medium whitespace-nowrap">No. of Pages</th>
                            <th className="py-2 pr-2 font-medium whitespace-nowrap">Size</th>
                            <th className="py-2 font-medium text-right w-20"> </th>
                          </tr>
                        </thead>
                        <tbody>
                          {productDocuments.map((u, index) => (
                            <tr key={u.id} className="border-b border-border/30 last:border-0">
                              <td className="py-2 pr-2 tabular-nums text-muted-foreground">{index + 1}</td>
                              <td className="py-2 pr-2 text-foreground/90 max-w-[180px] truncate" title={u.filename}>
                                {u.filename}
                              </td>
                              <td className="py-2 pr-2 text-foreground/90 max-w-[140px] truncate">
                                {u.documentTypeName?.trim() || "—"}
                              </td>
                              <td className="py-2 pr-2 capitalize text-foreground/90">
                                {u.documentCopy === "duplicate"
                                  ? "Duplicate"
                                  : u.documentCopy === "original"
                                    ? "Original"
                                    : "—"}
                              </td>
                              <td className="py-2 pr-2 tabular-nums text-foreground/90">
                                {u.numberOfPages == null ? "—" : u.numberOfPages}
                              </td>
                              <td className="py-2 pr-2 text-xs text-muted-foreground whitespace-nowrap">
                                {Math.round(u.size / 1024)} KB
                              </td>
                              <td className="py-2 text-right">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs text-destructive"
                                  onClick={() =>
                                    setDraft((d) => ({
                                      ...d,
                                      productDocuments: (d.productDocuments ?? []).filter((x) => x.id !== u.id),
                                    }))
                                  }
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </TabsContent>
              </Tabs>
            </motion.div>
          ) : currentStepId === "purchase" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 space-y-2">
              <SectionHeader
                title="Purchase & landed cost"
                subtitle="Enter pricing and deal details, then additional costs. Landed cost = base purchase + additional cost lines."
                icon={Wallet}
              />

              <div className="rounded-xl border border-border/50 bg-card/40 p-4 space-y-2.5">
                {/* Acquisition Type, Purchase Type, Import Country & Supplier Row */}
                <div className={cn(
                  "grid grid-cols-1 gap-4 pb-2 border-b border-border/40",
                  (draft.acquisitionType ?? "purchased") === "consignment"
                    ? "sm:grid-cols-1 md:max-w-xs"
                    : draft.purchaseType === "import"
                      ? "sm:grid-cols-2 lg:grid-cols-4"
                      : "sm:grid-cols-3"
                )}>
                  {/* Acquisition Type */}
                  <div className="space-y-1.5 min-w-0">
                    <Label className="text-xs font-semibold text-foreground">Acquisition Type</Label>
                    <div className="flex items-center gap-2 p-1 bg-muted/30 rounded-lg border border-border/40 h-11">
                      <button
                        type="button"
                        className={cn(
                          "flex-1 h-9 px-3 rounded-md text-xs font-semibold transition-all",
                          (draft.acquisitionType ?? "purchased") === "purchased"
                            ? "bg-background text-foreground shadow-xs border border-border/60"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                        onClick={() => {
                          setDraft((d) => ({
                            ...d,
                            acquisitionType: "purchased",
                          }));
                        }}
                      >
                        Purchased
                      </button>
                      <button
                        type="button"
                        className={cn(
                          "flex-1 h-9 px-3 rounded-md text-xs font-semibold transition-all",
                          draft.acquisitionType === "consignment"
                            ? "bg-background text-foreground shadow-xs border border-border/60"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                        onClick={() => {
                          setDraft((d) => ({
                            ...d,
                            acquisitionType: "consignment",
                          }));
                        }}
                      >
                        Consignment
                      </button>
                    </div>
                  </div>

                  {/* Purchase Type */}
                  {(draft.acquisitionType ?? "purchased") === "purchased" && (
                    <div className="space-y-1.5 min-w-0">
                      <Label className="text-xs font-semibold text-foreground">Purchase Type *</Label>
                      <div className="flex items-center gap-2 p-1 bg-muted/30 rounded-lg border border-border/40 h-11">
                        <button
                          type="button"
                          className={cn(
                            "flex-1 h-9 px-3 rounded-md text-xs font-semibold transition-all",
                            (draft.purchaseType ?? "local") === "local"
                              ? "bg-background text-foreground shadow-xs border border-border/60"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                          onClick={() =>
                            setDraft((d) => ({
                              ...d,
                              purchaseType: "local",
                              importCountryId: null,
                            }))
                          }
                        >
                          Local Purchase
                        </button>
                        <button
                          type="button"
                          className={cn(
                            "flex-1 h-9 px-3 rounded-md text-xs font-semibold transition-all",
                            draft.purchaseType === "import"
                              ? "bg-primary text-primary-foreground shadow-xs"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                          onClick={() => setDraft((d) => ({ ...d, purchaseType: "import" }))}
                        >
                          Import Purchase
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Import Country Selector (Shown when Import Purchase) */}
                  {(draft.acquisitionType ?? "purchased") === "purchased" && draft.purchaseType === "import" ? (
                    <div className="space-y-1.5 min-w-0">
                      <Label className="text-xs font-semibold text-foreground">Import Country *</Label>
                      <ImportCountrySelect
                        value={draft.importCountryId}
                        countries={countries}
                        placeholder="Select import country"
                        triggerClassName="h-11 text-sm"
                        onChange={(countryId) => {
                          const selectedCountry = countries.find((c) => c.id === countryId);
                          setDraft((d) => {
                            const countryCurrCode = selectedCountry?.currencyCode;
                            const activeCode = getActiveCompanyCurrency().code;
                            const nextCurrency = countryCurrCode || d.purchaseCurrency || activeCode;
                            const isBase = nextCurrency === activeCode;
                            const rate = isBase ? 1 : d.purchaseExchangeRate ?? 1;
                            const amt = d.purchaseCurrencyAmount ?? d.purchasePrice ?? null;
                            const basePrice = amt != null && rate != null ? amt * rate : null;

                            return {
                              ...d,
                              importCountryId: countryId,
                              purchaseCurrency: nextCurrency,
                              purchaseExchangeRate: rate,
                              purchaseCurrencyAmount: amt,
                              purchasePrice: basePrice,
                            };
                          });
                        }}
                      />
                    </div>
                  ) : null}

                  {/* Supplier Selector */}
                  {(draft.acquisitionType ?? "purchased") === "purchased" && (
                    <div className="space-y-1.5 min-w-0">
                      <Label className="text-xs font-semibold text-foreground">Supplier</Label>
                      <PartySelect
                        value={draft.supplierId ? String(draft.supplierId) : ""}
                        options={parties.filter((p) => {
                          const type = (p.type ?? "").toLowerCase();
                          return type === "supplier" || type === "all" || type === "both" || Boolean(p.isCompany);
                        })}
                        onChange={(val) => setDraft((d) => ({ ...d, supplierId: val || null }))}
                        placeholder="Select supplier"
                        triggerClassName="h-11 text-sm"
                      />
                    </div>
                  )}
                </div>

                {/* Purchased Currency, Purchased Amount & Conversion Rate Row (TOP ROW) */}
                {(draft.acquisitionType ?? "purchased") === "purchased" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 pb-2 border-b border-border/40">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Purchased Currency</Label>
                      <CurrencySelect
                        value={draft.purchaseCurrency ?? getActiveCompanyCurrency().code}
                        currencies={currencies}
                        onChange={(v) => {
                          const activeCode = getActiveCompanyCurrency().code;
                          const isBase = v === activeCode;
                          setDraft((d) => {
                            const rate = isBase ? 1 : d.purchaseExchangeRate ?? 1;
                            const amt = d.purchaseCurrencyAmount ?? d.purchasePrice ?? null;
                            const basePrice = amt != null && rate != null ? amt * rate : null;
                            return {
                              ...d,
                              purchaseCurrency: v,
                              purchaseExchangeRate: rate,
                              purchaseCurrencyAmount: amt,
                              purchasePrice: basePrice,
                            };
                          });
                        }}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-x-2 min-h-[1rem]">
                        <div className="min-w-0">
                          <Label className="text-xs text-muted-foreground">
                            Purchased Amount ({draft.purchaseCurrency ?? getActiveCompanyCurrency().code})
                          </Label>
                        </div>
                        {draft.purchaseCurrencyAmount && pkrAmountShortLabel(Number(draft.purchaseCurrencyAmount)) ? (
                          <span className="text-[11px] font-medium text-primary shrink-0 leading-none tabular-nums">
                            {pkrAmountShortLabel(Number(draft.purchaseCurrencyAmount))}
                          </span>
                        ) : null}
                      </div>
                      <Input
                        type="number"
                        className="glass-input h-10"
                        min={0}
                        step="any"
                        value={draft.purchaseCurrencyAmount == null ? "" : String(draft.purchaseCurrencyAmount)}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const amt = raw === "" ? null : Number(raw);
                          setDraft((d) => {
                            const rate = d.purchaseExchangeRate == null || !Number.isFinite(d.purchaseExchangeRate) ? 1 : d.purchaseExchangeRate;
                            const basePrice = amt != null && Number.isFinite(amt) ? amt * rate : null;
                            const isOwnerSurplus = d.dealType === "owner_vehicle" && d.profitRuleType === "owner_surplus";
                            return {
                              ...d,
                              purchaseCurrencyAmount: amt,
                              purchasePrice: basePrice,
                              ...(isOwnerSurplus && basePrice != null ? { ownerDemandPrice: basePrice } : {}),
                            };
                          });
                        }}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">
                        Conversion Rate ({draft.purchaseCurrency ?? getActiveCompanyCurrency().code} → {getActiveCompanyCurrency().code})
                      </Label>
                      <Input
                        type="number"
                        className="glass-input h-10"
                        min={0}
                        step="any"
                        value={draft.purchaseExchangeRate == null ? "" : String(draft.purchaseExchangeRate)}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const rate = raw === "" ? null : Number(raw);
                          setDraft((d) => {
                            const effectiveRate = rate == null || !Number.isFinite(rate) ? 1 : rate;
                            const amt = d.purchaseCurrencyAmount;
                            const basePrice = amt != null && Number.isFinite(amt) ? amt * effectiveRate : null;
                            const isOwnerSurplus = d.dealType === "owner_vehicle" && d.profitRuleType === "owner_surplus";
                            return {
                              ...d,
                              purchaseExchangeRate: rate,
                              purchasePrice: basePrice,
                              ...(isOwnerSurplus && basePrice != null ? { ownerDemandPrice: basePrice } : {}),
                            };
                          });
                        }}
                        placeholder="1.0000"
                      />
                    </div>

                    <div className="space-y-1.5 min-w-0">
                      <div className="flex items-center justify-between gap-x-2 min-h-[1rem]">
                        <div className="min-w-0">
                          <Label htmlFor="converted-base-purchase" className="text-xs text-muted-foreground">
                            Converted Base Purchase ({getActiveCompanyCurrency().code})
                          </Label>
                        </div>
                        {draft.purchasePrice && pkrAmountShortLabel(draft.purchasePrice) ? (
                          <span className="text-[11px] font-medium text-primary shrink-0 leading-none tabular-nums">
                            {pkrAmountShortLabel(draft.purchasePrice)}
                          </span>
                        ) : null}
                      </div>
                      <div className="relative min-w-0">
                        <Input
                          id="converted-base-purchase"
                          type="text"
                          readOnly
                          className="h-10 tabular-nums glass-input font-semibold text-foreground cursor-default"
                          value={formatCurrency(draft.purchasePrice ?? 0)}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Deal Type & Partner/Owner Selection Row (BOTTOM ROW) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Deal Type DDL */}
                  <div className="space-y-1.5 min-w-0">
                    <Label className="text-xs font-semibold text-foreground">Deal Type *</Label>
                    {draft.acquisitionType === "consignment" ? (
                      <Input
                        className="glass-input h-11 text-sm bg-muted/30 font-medium cursor-default"
                        readOnly
                        value="Seller Vehicle"
                      />
                    ) : (
                      <Select
                        value={draft.dealType ?? undefined}
                        onValueChange={(v) => {
                          const nextType = v as ProductDealType;
                          setDraft((d) => ({
                            ...d,
                            dealType: nextType,
                            ...clearedDealDependents(nextType),
                          }));
                        }}
                      >
                        <SelectTrigger className="glass-input h-11 text-sm">
                          <SelectValue placeholder="Select deal type" />
                        </SelectTrigger>
                        <SelectContent>
                          {DEAL_TYPE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Partner / Owner Selection */}
                  {effectiveDealType === "owner_vehicle" ? (
                    <div className="space-y-1.5 min-w-0">
                      <Label className="text-xs font-semibold text-foreground">Vehicle Owner / Seller *</Label>
                      <Select
                        value={draft.dealPartners?.[0]?.partyId ? String(draft.dealPartners[0].partyId) : ""}
                        onValueChange={(val) => {
                          const selectedParty = parties.find((p) => String(p.id) === String(val));
                          setDraft((d) => {
                            const currentPartners = d.dealPartners ?? [];
                            const first = currentPartners[0] ?? emptyDealPartnerRow();
                            const updatedFirst = {
                              ...first,
                              partyId: val,
                              partyName: selectedParty?.name || first.partyName,
                            };
                            return {
                              ...d,
                              dealPartners: [updatedFirst, ...currentPartners.slice(1)],
                            };
                          });
                        }}
                      >
                        <SelectTrigger className="glass-input h-11 text-sm">
                          <SelectValue placeholder="Select vehicle owner" />
                        </SelectTrigger>
                        <SelectContent>
                          {parties
                            .filter((p) => !p.isCompany || p.type === "supplier" || p.type === "both" || p.type === "owner")
                            .map((p) => (
                              <SelectItem key={p.id} value={String(p.id)}>
                                {p.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : effectiveDealType === "partnership" ? (
                    <div className="space-y-1.5 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs font-semibold text-foreground">Select Partners *</Label>
                        {canCreateInvestor ? (
                          <button
                            type="button"
                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10"
                            aria-label="Add investor"
                            title="Add investor"
                            onClick={() => setAddInvestorOpen(true)}
                          >
                            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                          </button>
                        ) : null}
                      </div>
                      {(() => {
                        const partnerParties = parties.filter((p) => {
                          const type = (p.type ?? "").toLowerCase();
                          return type === "investor" || type === "all" || type === "both" || Boolean(p.isCompany);
                        });
                        const selectedPartners = (draft.dealPartners ?? []).filter((dp) => dp.partyId);
                        const selectedPartnerIds = selectedPartners.map((dp) => String(dp.partyId));

                        const togglePartner = (partyId: string) => {
                          const targetParty = parties.find((p) => String(p.id) === String(partyId));
                          setDraft((d) => {
                            const currentRows = d.dealPartners ?? [];
                            const exists = currentRows.some((r) => String(r.partyId) === String(partyId));

                            let nextRows: DealPartnerDraft[];
                            if (exists) {
                              nextRows = currentRows.filter((r) => String(r.partyId) !== String(partyId));
                            } else {
                              const newRow: DealPartnerDraft = {
                                key: `dp-part-${partyId}-${Date.now()}`,
                                partyId: String(partyId),
                                partyName: targetParty?.name || `Partner #${partyId}`,
                                investmentAmount: null,
                                investmentRatio: null,
                                profitType: "percentage",
                                profitValue: null,
                                isCompanyOwner: false,
                              };
                              nextRows = [...currentRows.filter((r) => r.partyId), newRow];
                            }

                            if (nextRows.length > 0) {
                              const equalShare = Number((100 / nextRows.length).toFixed(2));
                              nextRows = nextRows.map((r) => ({
                                ...r,
                                profitType: "percentage" as const,
                                profitValue: equalShare,
                              }));
                            }

                            return {
                              ...d,
                              dealPartners: nextRows,
                            };
                          });
                        };

                        return (
                          <div>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="glass-input h-11 w-full justify-between text-sm font-normal"
                                >
                                  <span className="truncate">
                                    {selectedPartnerIds.length === 0
                                      ? "Select partners…"
                                      : `${selectedPartnerIds.length} partner${selectedPartnerIds.length > 1 ? "s" : ""} selected`}
                                  </span>
                                  <Search className="ml-2 h-4 w-4 shrink-0 opacity-70" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
                                align="start"
                                side="bottom"
                                sideOffset={4}
                                avoidCollisions={false}
                              >
                                <div className="border-b border-border/50 p-2">
                                  <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                      autoFocus
                                      className="h-9 pl-8 text-xs"
                                      placeholder="Search partners by name…"
                                      value={partnerSearch}
                                      onChange={(e) => setPartnerSearch(e.target.value)}
                                    />
                                  </div>
                                </div>
                                <div className="max-h-[220px] overflow-y-auto p-1">
                                  {(() => {
                                    const q = partnerSearch.trim().toLowerCase();
                                    const filteredParties = q
                                      ? partnerParties.filter((p) => (p.name ?? "").toLowerCase().includes(q))
                                      : partnerParties;

                                    if (filteredParties.length === 0) {
                                      return <p className="px-2 py-6 text-center text-xs text-muted-foreground">No partners found.</p>;
                                    }

                                    return filteredParties.map((p) => {
                                      const isSelected = selectedPartnerIds.includes(String(p.id));
                                      return (
                                        <button
                                          key={p.id}
                                          type="button"
                                          onClick={() => togglePartner(String(p.id))}
                                          className={cn(
                                            "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-muted/60",
                                            isSelected && "bg-muted/30 font-semibold text-foreground",
                                          )}
                                        >
                                          <span
                                            className={cn(
                                              "flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border transition-colors",
                                              isSelected
                                                ? "border-primary bg-primary text-primary-foreground shadow-2xs"
                                                : "border-input bg-background hover:border-accent-foreground/30",
                                            )}
                                          >
                                            {isSelected ? <Check className="h-3 w-3 text-primary-foreground stroke-[2.5]" /> : null}
                                          </span>
                                          <span className="flex-1 truncate text-xs">{p.name}</span>
                                        </button>
                                      );
                                    });
                                  })()}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        );
                      })()}
                    </div>
                  ) : effectiveDealType === "purchased" ? (
                    <div className="space-y-1.5 min-w-0">
                      <Label className="text-xs font-semibold text-foreground">Purchased By (Showroom)</Label>
                      <Input
                        className="glass-input h-11 text-sm bg-muted/30 font-medium cursor-default"
                        readOnly
                        value={companyParties[0]?.name || "Company Showroom"}
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="rounded-xl border border-border/50 bg-card/50 p-4 space-y-4">
                <div className="rounded-lg border border-border/50 bg-background/60 p-3.5 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 pb-2.5">
                    <div>
                      <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                        <span>Additional cost lines</span>
                        {canCreatePurchaseCostType ? (
                          <button
                            type="button"
                            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10"
                            aria-label="Add cost type"
                            title="Add cost type"
                            onClick={() => openCostTypeDialog(null)}
                          >
                            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                          </button>
                        ) : null}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Types from{" "}
                        <Link to="/purchase-cost-types" className="text-primary font-medium hover:underline">
                          Purchase cost types
                        </Link>
                      </p>
                    </div>

                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      className="gap-1.5 shrink-0 h-8 font-medium shadow-xs"
                      onClick={openAddCostLineModal}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add line
                    </Button>
                  </div>

                  {purchaseCostTypes.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2">
                      No cost types yet.{" "}
                      {canCreatePurchaseCostType ? (
                        <button
                          type="button"
                          className="text-primary font-medium hover:underline"
                          onClick={() => openCostTypeDialog(null)}
                        >
                          Add a cost type
                        </button>
                      ) : (
                        <Link to="/purchase-cost-types" className="text-primary font-medium hover:underline">
                          Add purchase cost types
                        </Link>
                      )}
                      .
                    </p>
                  ) : (draft.purchaseCostLines ?? []).length === 0 ? (
                    <div className="text-xs text-muted-foreground rounded-lg bg-muted/20 border border-border/40 p-4 text-center">
                      <p className="font-medium text-foreground/80">No additional cost lines added.</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">Click "+ Add line" to record duty, freight, or other expense lines.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border/40 rounded-lg border border-border/40 bg-card overflow-hidden">
                      {(draft.purchaseCostLines ?? []).map((line, i) => {
                        const typeRec = purchaseCostTypes.find((t) => t.id === line.typeId);
                        const typeName = typeRec?.label ?? line.typeLabel ?? "Cost Line";
                        const paidByLabel = paidByOptions.find((o) => o.value === paidBySelectValue(line))?.label ?? "Company";
                        const paidToParty = parties.find((p) => String(p.id) === String(line.paidToPartyId ?? ""));
                        const paidToLabel = paidToParty?.name ?? null;
                        const costBearerLabel = (() => {
                          if (effectiveDealType === "purchased") {
                            return companyParties[0]?.name || "Showroom";
                          }
                          if (effectiveDealType === "owner_vehicle") {
                            return draft.dealPartners?.[0]?.partyName || "Vehicle Owner";
                          }
                          return "Partnership (Split)";
                        })();
                        const currCode = line.currencyCode ?? draft.purchaseCurrency ?? getActiveCompanyCurrency().code;

                        return (
                          <div key={`${line.typeId}-${i}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 text-xs hover:bg-muted/10 transition-colors">
                            <div className="space-y-1 min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-bold text-foreground text-sm">{typeName}</span>
                                {line.paidByPartyId != null ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20" title={`Paid by ${paidByLabel}`}>
                                    Paid ({paidByLabel})
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                                    Pending
                                  </span>
                                )}
                                {paidToLabel && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20" title="Pay to / Supplier">
                                    Supplier: {paidToLabel}
                                  </span>
                                )}
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20" title="Cost bearer">
                                  Bearer: {costBearerLabel}
                                </span>
                              </div>

                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                                <span>
                                  Currency: <strong className="text-foreground">{currCode} {line.currencyAmount ?? 0}</strong>
                                  {currCode !== getActiveCompanyCurrency().code && line.exchangeRate ? (
                                    <span className="text-muted-foreground ml-1">(@ {line.exchangeRate})</span>
                                  ) : null}
                                </span>
                                {line.bankName ? (
                                  <span>
                                    Bank: <strong className="text-foreground">{line.bankName}</strong>
                                  </span>
                                ) : null}
                                {line.paymentMethod === "online" && line.transactionReferenceNo ? (
                                  <span>
                                    Txn Ref: <strong className="text-foreground">{line.transactionReferenceNo}</strong>
                                  </span>
                                ) : null}
                                {line.paymentMethod === "pay_order" && line.payOrderNo ? (
                                  <span>
                                    PO No: <strong className="text-foreground">{line.payOrderNo}</strong>
                                  </span>
                                ) : null}
                                {line.description ? (
                                  <span className="italic truncate max-w-xs">"{line.description}"</span>
                                ) : null}
                              </div>
                            </div>

                            <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-border/30">
                              <div className="text-right">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Converted Cost</p>
                                <p className="font-bold text-foreground text-sm tabular-nums">{formatCurrency(line.amount)}</p>
                              </div>

                              <div className="flex items-center gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                                  onClick={() => openEditCostLineModal(i)}
                                  aria-label="Edit line"
                                  title="Edit cost line"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive/80 hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => setPendingCostLineRemoveIndex(i)}
                                  aria-label="Remove line"
                                  title="Remove cost line"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Summary Subsection directly below additional cost lines */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-start pt-1">
                  <div className="space-y-1.5 min-w-0">
                    <Label className="text-xs text-muted-foreground">
                      Additional Costs ({getActiveCompanyCurrency().code})
                    </Label>
                    <Input
                      type="text"
                      readOnly
                      className="h-10 tabular-nums glass-input font-semibold text-foreground cursor-default"
                      value={formatCurrency(additionalSubtotal)}
                    />
                  </div>

                  <div className="space-y-1.5 min-w-0">
                    <div className="flex items-center justify-between gap-x-2 min-h-[1rem]">
                      <div className="min-w-0">
                        <Label htmlFor="total-landed-cost" className="text-xs font-semibold text-primary">
                          Total Landed Cost ({getActiveCompanyCurrency().code})
                        </Label>
                      </div>
                      {landedPreview && pkrAmountShortLabel(landedPreview) ? (
                        <span className="text-[11px] font-semibold text-primary shrink-0 leading-none tabular-nums">
                          {pkrAmountShortLabel(landedPreview)}
                        </span>
                      ) : null}
                    </div>
                    <Input
                      id="total-landed-cost"
                      type="text"
                      readOnly
                      className="h-10 tabular-nums glass-input font-bold text-foreground cursor-default border-primary/40 bg-primary/5"
                      value={formatCurrency(landedPreview ?? 0)}
                    />
                  </div>

                  <SalePriceInput
                    id="sale-price"
                    label="Expected Sale Price (optional)"
                    value={draft.salePrice ?? null}
                    onChange={(salePrice) => setDraft((d) => ({ ...d, salePrice }))}
                    inputClassName="glass-input"
                    showShortLabelHint
                    className="min-w-0"
                  />
                </div>
              </div>

              {/* Detailed Deal Information & Deal Partners Section in Original Position */}
              <div className="space-y-2">
                {effectiveDealType !== "purchased" && effectiveDealType !== "partnership" && (
                  <DealInformationSection
                    draft={draft}
                    onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
                  />
                )}
                {effectiveDealType !== "purchased" && !isOwnerSurplus && (
                  <CompanyOwnerCommissionSection
                    value={draft.showroomCharge ?? draft.companyOwnerCommission}
                    companyParties={companyParties}
                    commissionBase={showroomCommissionBase}
                    dealType={effectiveDealType}
                    profitRuleType={draft.profitRuleType}
                    acquisitionType={draft.acquisitionType}
                    locked={isOwnerSurplus}
                    onChange={(showroomCharge) => {
                      setDraft((d) => ({
                        ...d,
                        showroomCharge,
                        companyOwnerCommission: showroomCharge,
                      }));
                    }}
                  />
                )}
                {effectiveDealType === "partnership" && (
                  <DealPartnersSection
                    dealType={effectiveDealType}
                    profitRuleType={draft.profitRuleType}
                    partners={draft.dealPartners ?? []}
                    parties={parties}
                    companyParties={companyParties}
                    investmentPool={partnerInvestmentPool}
                    ownerSurplusDefaults={isOwnerSurplus}
                    onPartiesChange={setParties}
                    onChange={(dealPartners) => {
                      setDraft((d) => ({
                        ...d,
                        dealPartners: withRecalculatedInvestmentRatios(
                          dealPartners,
                          partnerInvestmentPool,
                        ),
                      }));
                    }}
                  />
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Purchase Notes (optional)</Label>
                <Textarea
                  className="glass-input min-h-[88px]"
                  value={draft.purchaseNotes ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, purchaseNotes: e.target.value }))}
                  placeholder="Purchase notes…"
                  aria-label="Purchase notes"
                />
              </div>
            </motion.div>
          ) : null
        }

        <div className="flex flex-col-reverse gap-3 border-t border-border/40 pb-4 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="glass"
            className="w-full gap-2 sm:w-auto"
            disabled={wizardStep === 0}
            onClick={goBack}
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end">
            {isLastStep ? (
              <>
                <Button
                  type="button"
                  variant={(draft.inventoryStatus ?? "draft") === "draft" ? "glass" : "premium"}
                  className="w-full gap-2 px-8 sm:w-auto"
                  disabled={saveLoading || isEditLocked}
                  title={isEditLocked ? "Sold or reserved products cannot be edited." : undefined}
                  onClick={() => void finishWizard(false)}
                >
                  {saveLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save
                </Button>
                {(draft.inventoryStatus ?? "draft") === "draft" ? (
                  <Button
                    type="button"
                    variant="premium"
                    className="w-full gap-2 px-8 sm:w-auto"
                    disabled={saveLoading || isEditLocked}
                    title={isEditLocked ? "Sold or reserved products cannot be edited." : undefined}
                    onClick={() => void finishWizard(true)}
                  >
                    {saveLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save &amp; Publish
                  </Button>
                ) : null}
              </>
            ) : (
              <Button
                type="button"
                variant="premium"
                className="w-full gap-2 px-8 sm:w-auto"
                disabled={saveLoading || isEditLocked}
                title={isEditLocked ? "Sold or reserved products cannot be edited." : undefined}
                onClick={() => void goNext()}
              >
                {saveLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save &amp; Next
              </Button>
            )}
          </div>
        </div>
        </div>
      </div>

      <VehicleProductNameModal
        open={vehiclePickerOpen}
        onOpenChange={setVehiclePickerOpen}
        brands={brands}
        initialBrandId={draft.brandId ?? null}
        initialModelYear={draft.modelYear ?? null}
        initialVehicleModelId={draft.vehicleModelId ?? null}
        initialVehicleVersionId={draft.vehicleVersionId ?? null}
        onBrandCreated={(brand) => {
          setBrands((prev) => (prev.some((b) => b.id === brand.id) ? prev : [...prev, brand]));
        }}
        onApply={({ name, brandId, vehicleModelId, vehicleVersionId, modelYear }) => {
          setDraft((d) => ({
            ...d,
            name,
            brandId,
            vehicleModelId,
            vehicleVersionId,
            modelYear,
            slug: slugify(name),
          }));
        }}
      />

      <Dialog
        open={costTypeDialogOpen}
        onOpenChange={(open) => {
          if (costTypeSaving) return;
          setCostTypeDialogOpen(open);
          if (!open) {
            setCostTypeDialogLineIndex(null);
            setCostTypeLabel("");
            setCostTypeCategoryId("");
          }
        }}
      >
        <DialogContent className="sm:max-w-xl md:max-w-2xl p-6 gap-5">
          <DialogHeader className="border-b border-border/40 pb-3">
            <DialogTitle className="flex items-center gap-2.5 text-lg font-bold text-foreground">
              <Tag className="h-5 w-5 text-primary shrink-0" />
              New Cost Type
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-1 text-xs">
            <div className="space-y-4 bg-muted/20 p-4 rounded-xl border border-border/40">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Category *</Label>
                <PurchaseCostCategorySelect
                  value={costTypeCategoryId}
                  categories={purchaseCostCategories}
                  onChange={setCostTypeCategoryId}
                  placeholder="Search or select category"
                  disabled={costTypeSaving}
                  triggerClassName="h-11 text-sm"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Manage categories in{" "}
                  <Link to="/purchase-cost-categories" target="_blank" className="text-primary hover:underline font-medium">
                    Purchase cost categories
                  </Link>
                  .
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="quick-cost-type-label" className="text-xs font-semibold text-foreground">
                  Cost Type Name *
                </Label>
                <Input
                  id="quick-cost-type-label"
                  className="glass-input h-11 text-sm font-medium"
                  value={costTypeLabel}
                  disabled={costTypeSaving}
                  placeholder="e.g. Custom Duty, Transportation, Documentation"
                  onChange={(e) => setCostTypeLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void handleCreateCostType();
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row items-center justify-end gap-2 pt-3 border-t border-border/40">
            <Button
              type="button"
              variant="ghost"
              className="h-10 px-4 text-xs font-medium"
              disabled={costTypeSaving}
              onClick={() => setCostTypeDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              className="h-10 px-5 text-xs font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs"
              disabled={costTypeSaving}
              onClick={() => void handleCreateCostType()}
            >
              {costTypeSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={documentTypeDialogOpen}
        onOpenChange={(open) => {
          if (documentTypeSaving) return;
          setDocumentTypeDialogOpen(open);
          if (!open) setDocumentTypeName("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New document type</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="quick-document-type-name">Name</Label>
              <Input
                id="quick-document-type-name"
                className="glass-input"
                value={documentTypeName}
                disabled={documentTypeSaving}
                placeholder="e.g. Fitness Certificate"
                autoFocus
                onChange={(e) => setDocumentTypeName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void handleCreateDocumentType();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={documentTypeSaving}
              onClick={() => setDocumentTypeDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              disabled={documentTypeSaving}
              onClick={() => void handleCreateDocumentType()}
            >
              {documentTypeSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Additional Cost Line Add/Edit Modal - Larger & Premium Layout */}
      <Dialog open={costLineModalOpen} onOpenChange={setCostLineModalOpen}>
        <DialogContent className="sm:max-w-2xl md:max-w-3xl p-6 gap-5">
          <DialogHeader className="border-b border-border/40 pb-3">
            <DialogTitle className="flex items-center gap-2.5 text-lg font-bold text-foreground">
              <Wallet className="h-5 w-5 text-primary shrink-0" />
              {editingCostLineIndex != null ? "Edit Additional Cost Line" : "Add Additional Cost Line"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-1 text-xs">
            {/* Section 1: Cost Type & Currency */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-muted/20 p-4 rounded-xl border border-border/40">
              {/* Cost Type */}
              <div className="space-y-1.5 min-w-0">
                <Label className="flex items-center justify-between gap-1 text-xs font-semibold text-foreground">
                  <span>Cost Type *</span>
                  {canCreatePurchaseCostType ? (
                    <button
                      type="button"
                      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10"
                      aria-label="Add cost type"
                      title="Add cost type"
                      onClick={() => openCostTypeDialog(editingCostLineIndex)}
                    >
                      <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </button>
                  ) : null}
                </Label>
                {(() => {
                  const takenTypeIds = (draft.purchaseCostLines ?? [])
                    .filter((_, j) => j !== editingCostLineIndex)
                    .map((l) => String(l.typeId))
                    .filter(Boolean);

                  return (
                    <PurchaseCostTypeSelect
                      value={costLineModalForm.typeId}
                      types={purchaseCostTypes}
                      takenTypeIds={takenTypeIds}
                      onChange={(v) => setCostLineModalForm((f) => ({ ...f, typeId: v }))}
                      placeholder="Select cost type"
                      triggerClassName="h-11 text-sm"
                    />
                  );
                })()}
              </div>

              {/* Currency */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Currency</Label>
                <CurrencySelect
                  value={costLineModalForm.currencyCode ?? draft.purchaseCurrency ?? getActiveCompanyCurrency().code}
                  currencies={currencies}
                  triggerClassName="h-11 text-sm"
                  onChange={(v) => {
                    const activeBase = getActiveCompanyCurrency().code;
                    const isBase = v === activeBase;
                    setCostLineModalForm((f) => ({
                      ...f,
                      currencyCode: v,
                      exchangeRate: isBase ? 1 : f.exchangeRate ?? 1,
                    }));
                  }}
                />
              </div>
            </div>

            {/* Section 2: Financial Amounts & Rates with Price in Words */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-primary/5 p-4 rounded-xl border border-primary/20">
              {/* Amount */}
              <div className="space-y-1.5 min-w-0">
                <div className="flex items-center justify-between gap-x-2 min-h-[1.25rem]">
                  <Label className="text-xs font-semibold text-foreground">Amount</Label>
                  {costLineModalForm.currencyAmount && pkrAmountShortLabel(Number(costLineModalForm.currencyAmount)) ? (
                    <span className="text-[11px] font-bold text-primary shrink-0 leading-none tabular-nums px-2 py-0.5 rounded bg-primary/10 border border-primary/20">
                      {pkrAmountShortLabel(Number(costLineModalForm.currencyAmount))}
                    </span>
                  ) : null}
                </div>
                <Input
                  type="number"
                  className="glass-input h-11 text-sm font-medium"
                  min={0}
                  step="any"
                  value={costLineModalForm.currencyAmount == null ? "" : String(costLineModalForm.currencyAmount)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const n = raw === "" ? 0 : Number(raw);
                    setCostLineModalForm((f) => ({
                      ...f,
                      currencyAmount: Number.isFinite(n) ? n : 0,
                    }));
                  }}
                  placeholder="0.00"
                />
              </div>

              {/* Conversion Rate */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-x-2 min-h-[1.25rem]">
                  <Label className="text-xs font-semibold text-foreground">Conversion Rate</Label>
                </div>
                <Input
                  type="number"
                  className="glass-input h-11 text-sm"
                  min={0}
                  step="any"
                  disabled={(costLineModalForm.currencyCode ?? getActiveCompanyCurrency().code) === getActiveCompanyCurrency().code}
                  value={costLineModalForm.exchangeRate == null ? "" : String(costLineModalForm.exchangeRate)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const n = raw === "" ? null : Number(raw);
                    setCostLineModalForm((f) => ({
                      ...f,
                      exchangeRate: n != null && Number.isFinite(n) ? n : null,
                    }));
                  }}
                  placeholder="1.0000"
                />
              </div>

              {/* Converted Base Cost with Price in Words */}
              {(() => {
                const cCode = costLineModalForm.currencyCode ?? getActiveCompanyCurrency().code;
                const isBase = cCode === getActiveCompanyCurrency().code;
                const exRate = isBase ? 1 : costLineModalForm.exchangeRate ?? 1;
                const amt = costLineModalForm.currencyAmount ?? 0;
                const baseCalc = isBase ? amt : Number((amt * exRate).toFixed(2));
                const shortLabel = baseCalc ? pkrAmountShortLabel(baseCalc) : null;

                return (
                  <div className="space-y-1.5 min-w-0">
                    <div className="flex items-center justify-between gap-x-2 min-h-[1.25rem]">
                      <Label className="text-xs font-semibold text-foreground">
                        Cost Base ({getActiveCompanyCurrency().code})
                      </Label>
                      {shortLabel ? (
                        <span className="text-[11px] font-bold text-primary shrink-0 leading-none tabular-nums px-2 py-0.5 rounded bg-primary/10 border border-primary/20">
                          {shortLabel}
                        </span>
                      ) : null}
                    </div>
                    <Input
                      type="text"
                      readOnly
                      className="glass-input h-11 text-sm font-bold tabular-nums cursor-default bg-background text-foreground border-primary/40"
                      value={formatCurrency(baseCalc)}
                    />
                  </div>
                );
              })()}
            </div>

            {/* Section 3: Responsibility details */}
            <div className="space-y-3 bg-muted/20 p-4 rounded-xl border border-border/40">
              {/* Pay To / Supplier */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Pay To / Supplier</Label>
                <PartySelect
                  value={costLineModalForm.paidToPartyId ? String(costLineModalForm.paidToPartyId) : ""}
                  options={parties.filter((p) => {
                    if (costLineModalForm.paidToPartyId && String(p.id) === String(costLineModalForm.paidToPartyId)) {
                      return true;
                    }
                    const type = (p.type ?? "").toLowerCase();
                    return type !== "customer";
                  })}
                  onChange={(val) =>
                    setCostLineModalForm((f) => ({
                      ...f,
                      paidToPartyId: val ? Number(val) : null,
                    }))
                  }
                  placeholder="Select supplier"
                  triggerClassName="h-11 text-sm bg-background border-border/40"
                />
              </div>
            </div>

            {/* Section 4: Notes */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Notes (optional)</Label>
              <Input
                type="text"
                className="glass-input h-11 text-sm"
                value={costLineModalForm.description ?? ""}
                onChange={(e) => setCostLineModalForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional notes or reference..."
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row items-center justify-end gap-2 pt-3 border-t border-border/40">
            <Button
              type="button"
              variant="ghost"
              className="h-10 px-4 text-xs font-medium"
              onClick={() => setCostLineModalOpen(false)}
            >
              Cancel
            </Button>

            {editingCostLineIndex == null ? (
              <Button
                type="button"
                variant="outline"
                className="h-10 px-4 text-xs font-semibold border-primary/40 text-primary hover:bg-primary/10 shadow-2xs"
                onClick={() => handleSaveCostLineModal(true)}
              >
                Save and Add More
              </Button>
            ) : null}

            <Button
              type="button"
              variant="default"
              className="h-10 px-5 text-xs font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs"
              onClick={() => handleSaveCostLineModal(false)}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateInvestorDialog
        open={addInvestorOpen}
        onOpenChange={setAddInvestorOpen}
        onCreated={async (record) => {
          try {
            const nextParties = await syncPartiesFromApi();
            setParties(nextParties.length ? nextParties : listPartyOptions());
            const partyId = String(record.id);
            const partyName = record.name?.trim() || `Partner #${partyId}`;
            setDraft((d) => {
              const currentRows = d.dealPartners ?? [];
              if (currentRows.some((r) => String(r.partyId) === partyId)) {
                return d;
              }
              const newRow: DealPartnerDraft = {
                key: `dp-part-${partyId}-${Date.now()}`,
                partyId,
                partyName,
                investmentAmount: null,
                investmentRatio: null,
                profitType: "percentage",
                profitValue: null,
                isCompanyOwner: false,
              };
              let nextRows = [...currentRows.filter((r) => r.partyId), newRow];
              if (nextRows.length > 0) {
                const equalShare = Number((100 / nextRows.length).toFixed(2));
                nextRows = nextRows.map((r) => ({
                  ...r,
                  profitType: "percentage" as const,
                  profitValue: equalShare,
                }));
              }
              return { ...d, dealPartners: nextRows };
            });
          } catch (error) {
            console.error("[AddProduct] Failed to refresh parties after investor create", error);
            toast.error("Investor saved, but the party list could not be refreshed. Reload the page.");
          }
        }}
      />

      <ConfirmAlertDialog
        open={pendingCostLineRemoveIndex !== null}
        onOpenChange={(o) => !o && setPendingCostLineRemoveIndex(null)}
        title="Remove this cost line?"
        description={
          pendingCostLineRemoveIndex !== null ? (
            <>
              <span>
                Remove{" "}
                <strong className="text-foreground font-semibold">
                  {purchaseCostTypes.find(
                    (t) => t.id === draft.purchaseCostLines?.[pendingCostLineRemoveIndex]?.typeId,
                  )?.label ?? "this line"}
                </strong>{" "}
                from the landed cost breakdown?
              </span>
              {draft.purchaseCostLines?.[pendingCostLineRemoveIndex]?.description?.trim() ? (
                <span className="block mt-2 text-xs text-muted-foreground">
                  Description: {draft.purchaseCostLines[pendingCostLineRemoveIndex]?.description?.trim()}
                </span>
              ) : null}
            </>
          ) : (
            ""
          )
        }
        mode="confirm"
        variant="danger"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (pendingCostLineRemoveIndex !== null) removeCostLine(pendingCostLineRemoveIndex);
          setPendingCostLineRemoveIndex(null);
        }}
      />
    </Layout>
  );
};

export default AddProduct;
